/** Cloud API client — SaaS console ↔ cloud-min */

import {
  capabilitiesForForm,
  type AuthScope,
  type AuditCategory,
  type AuditEntry,
  type DeliveryStatus,
  type Project,
  type StaffUser,
} from "../data/mock";

const TOKEN_KEY = "yunshu.cloud_token";
const SESSION_PASS_KEY = "yunshu.auth_pass_session";
/** 运营跨租户调试：绑定目标租户 search_path（X-UDHUB-Tenant-Id） */
const OPS_TENANT_KEY = "yunshu.ops_tenant_id";

export function apiBase(): string {
  const base = import.meta.env.VITE_API_BASE || "/api/v1";
  return base.replace(/\/+$/, "");
}

export function getOpsTenantId(): string | null {
  try {
    return sessionStorage.getItem(OPS_TENANT_KEY);
  } catch {
    return null;
  }
}

/** 平台运营进入某集成商项目调试前调用；传 null 清除 */
export function setOpsTenantId(tenantId: string | null) {
  try {
    if (tenantId && /^tn_[a-z0-9_]+$/i.test(tenantId) && tenantId !== "tn_platform") {
      sessionStorage.setItem(OPS_TENANT_KEY, tenantId);
    } else {
      sessionStorage.removeItem(OPS_TENANT_KEY);
    }
  } catch {
    /* ignore */
  }
}

function opsTenantHeaders(path?: string): Record<string, string> {
  // 仅项目数据面带跨租户头。/admin、/settings、/notifications、/elevated-sessions 等
  // 必须留在平台 JWT 租户，否则运营进调试后全局轮询会打错 schema → 401 清会话。
  if (!path || !path.startsWith("/projects/")) {
    return {};
  }
  const tid = getOpsTenantId();
  return tid ? { "X-UDHUB-Tenant-Id": tid } : {};
}

function getSessionPassword(): string | null {
  try {
    return sessionStorage.getItem(SESSION_PASS_KEY);
  } catch {
    return null;
  }
}

export function setSessionPassword(password: string | null) {
  try {
    if (password) sessionStorage.setItem(SESSION_PASS_KEY, password);
    else sessionStorage.removeItem(SESSION_PASS_KEY);
  } catch {
    /* ignore */
  }
}

async function fetchWithTimeout(
  url: string,
  init: RequestInit = {},
  timeoutMs = 8000,
): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

export function getCloudToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setCloudToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

const AUTH_EMAIL_KEY = "yunshu.auth";

/** Clear bearer token + console login shell (e.g. after 401). */
export function clearCloudAuth() {
  setCloudToken(null);
  setSessionPassword(null);
  try {
    localStorage.removeItem(AUTH_EMAIL_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event("yunshu:auth-cleared"));
}

export async function cloudHealth(): Promise<boolean> {
  const url = `${apiBase()}/health`;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const res = await fetchWithTimeout(url, {}, 8000);
      if (res.ok) return true;
    } catch {
      /* retry */
    }
    if (attempt < 2) {
      await new Promise((resolve) =>
        window.setTimeout(resolve, 350 * (attempt + 1)),
      );
    }
  }
  return false;
}

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => ({}));
}

function unwrapApi<T>(body: unknown): T {
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    "code" in (body as Record<string, unknown>)
  ) {
    return (body as { data: T }).data;
  }
  return body as T;
}

/** 从 cloud-api 错误体提取业务码（not_found / auth_required 等） */
export function apiErrorFromResponse(
  raw: unknown,
): { code: string; message: string } {
  const body = raw as Record<string, unknown>;
  if (body?.error && typeof body.error === "object") {
    const e = body.error as { code?: string; message?: string };
    const message = String(e.message || e.code || "request_failed");
    return { code: message, message };
  }
  if (typeof body?.error === "string") {
    return {
      code: body.error,
      message: typeof body.message === "string" ? body.message : body.error,
    };
  }
  if (typeof body?.message === "string") {
    return { code: body.message, message: body.message };
  }
  return { code: "request_failed", message: "request_failed" };
}

export function apiErrorCode(err: unknown): string {
  if (!err || typeof err !== "object") return "unknown";
  const e = err as { code?: unknown; message?: string };
  if (typeof e.code === "string") return e.code;
  if (e.code && typeof e.code === "object") {
    const nested = e.code as { message?: string; code?: string };
    return String(nested.message || nested.code || "unknown");
  }
  if (typeof e.message === "string") return e.message;
  return "unknown";
}

export function apiErrorMessage(err: unknown, fallback = "操作失败"): string {
  const code = apiErrorCode(err);
  const rawMessage =
    err instanceof Error && typeof err.message === "string" ? err.message.trim() : "";
  const humanMessage =
    rawMessage && rawMessage !== code && rawMessage !== "request_failed"
      ? rawMessage
      : "";

  if (code === "no_token") return "请先登录云枢控制台";
  if (code === "auth_required" || code === "login_failed") {
    return "登录已失效，请重新登录";
  }
  if (code === "not_found") return "未找到待认领主机，请确认激活码或手机号";
  if (code === "activation_expired") return "激活码已过期，请重新生成";
  if (code === "already_claimed") return "该激活码/手机号已被认领";
  if (code === "ambiguous_project") {
    return "存在多个同名未绑定客户，请从列表中选择";
  }
  if (code === "invalid_code_format") return "激活码格式无效（应为 8 位）";
  if (code === "activation_code_required") return "请填写主机激活码";
  if (code === "email_exists") return "该邮箱已注册，请直接登录";
  if (code === "register_closed")
    return "公开注册已关闭，请联系平台开通集成商账号";
  if (code === "admin_required")
    return "请填写初始管理员姓名、邮箱与密码";
  if (code === "invalid_plan") return "套餐无效";
  if (code === "email_unverified") return "请先完成邮箱验证";
  if (code === "legal_name_required") return "请填写营业执照上的企业名称";
  if (code === "uscc_required") return "请填写统一社会信用代码";
  if (code === "uscc_length") return "统一社会信用代码须为 18 位";
  if (code === "uscc_charset") return "统一社会信用代码含非法字符";
  if (code === "uscc_checksum") return "统一社会信用代码校验位不正确";
  if (code === "uscc_taken") return "该统一社会信用代码已被其他租户使用";
  if (code === "already_verified") return "企业已认证，无需重复提交";
  if (code === "already_pending") return "认证审核中，请耐心等待";
  if (code === "not_pending") return "当前不在待审核状态";
  if (code === "note_required") return "驳回时请填写原因";
  if (code === "rate_limited") {
    return humanMessage || "发送过于频繁，请稍后再试";
  }
  if (code === "mail_failed") return "邮件发送失败，请稍后重试";
  if (code === "invalid_code") return "验证码错误或已过期";
  if (code === "invalid_email") return "请输入有效邮箱";
  if (code === "password_too_short") return "密码至少 8 位";
  if (code === "password_unchanged") return "新密码不能与当前密码相同";
  if (code === "invalid_password") return "当前密码不正确";
  if (code === "last_admin") return "请先移交管理员权限再删除账号";
  if (code === "platform_admin_locked") return "平台管理员不可自助删除";
  if (code === "builtin_locked") return "系统预置角色不可删除";
  if (code === "company_required") return "请填写公司 / 集成商名称";
  if (code === "name_required") return "请填写联系人姓名";
  if (code === "aqara_app_key_required") return "首次保存必须填写 App Key";
  if (code === "aqara_config_invalid") return "请检查 Aqara 配置字段格式";
  if (code === "aqara_crypto_unavailable") {
    return "Aqara 安全存储尚未就绪，请联系运维配置加密密钥";
  }
  if (code === "aqara_account_required") {
    return humanMessage || "请输入客户 Aqara 账号（手机号或邮箱）";
  }
  if (code === "aqara_auth_code_required") {
    return humanMessage || "请输入验证码";
  }
  if (code === "connection_not_found") {
    return humanMessage || "项目 Aqara 连接未初始化，请刷新后重试";
  }
  if (
    code === "aqara_api_error" ||
    code === "aqara_http_error" ||
    code === "aqara_app_not_configured" ||
    code === "aqara_not_connected" ||
    code === "aqara_not_authorized"
  ) {
    return humanMessage || fallback;
  }
  if (code === "request_failed" || code === "unknown") {
    return humanMessage || fallback;
  }
  // Prefer Chinese / longer server message over opaque snake_case codes.
  if (humanMessage && (/[\u4e00-\u9fff]/.test(humanMessage) || humanMessage.length > code.length)) {
    return humanMessage;
  }
  return code;
}

function throwApiError(raw: unknown, status: number): never {
  const { code, message } = apiErrorFromResponse(raw);
  throw Object.assign(new Error(message), { code, status, data: raw });
}

export async function cloudLogin(email: string, password: string) {
  const res = await fetchWithTimeout(`${apiBase()}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error("login_failed");
  const raw = await parseJson(res);
  const data = unwrapApi<{
    accessToken?: string;
    access_token?: string;
    user?: {
      email: string;
      name?: string;
      role: string;
      tenantId: string;
      tenantName?: string;
    };
    tenant?: { id: string; name: string };
  }>(raw);
  const token = data.accessToken ?? data.access_token;
  if (!token) throw new Error("login_failed");
  setCloudToken(token);
  setSessionPassword(password);
  return {
    access_token: token,
    user: data.user || null,
    tenant: data.tenant || null,
  };
}

export async function cloudGetMe() {
  return (await authedFetch("/auth/me")) as {
    id: string;
    email: string;
    name: string;
    role: string;
    tenantId: string;
    tenantName?: string;
    phone?: string | null;
    phoneBound?: boolean;
    wxBound?: boolean;
  };
}

export async function cloudPhoneSendCode(
  phone: string,
  purpose: "login" | "bind" = "login",
) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getCloudToken();
  if (purpose === "bind" && token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetchWithTimeout(`${apiBase()}/auth/phone/send-code`, {
    method: "POST",
    headers,
    body: JSON.stringify({ phone, purpose }),
  });
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  return unwrapApi<{
    ok: boolean;
    delivered?: boolean;
    message: string;
    expiresInSec: number;
    devCode?: string;
  }>(raw);
}

export async function cloudPhoneLogin(phone: string, code: string) {
  const res = await fetchWithTimeout(`${apiBase()}/auth/phone/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone, code }),
  });
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  const data = unwrapApi<{
    accessToken?: string;
    user?: {
      email: string;
      name?: string;
      role: string;
      tenantId: string;
      tenantName?: string;
      phone?: string;
    };
    tenant?: { id: string; name: string };
  }>(raw);
  const accessToken = data.accessToken;
  if (!accessToken) throw new Error("phone_login_failed");
  setCloudToken(accessToken);
  setSessionPassword(null);
  return data;
}

export async function cloudPhoneBind(phone: string, code: string) {
  return (await authedFetch("/auth/phone/bind", {
    method: "POST",
    body: JSON.stringify({ phone, code }),
  })) as { ok: boolean; phone: string; message?: string };
}

export async function cloudRegister(body: {
  email: string;
  password: string;
  name: string;
  companyName: string;
}) {
  const res = await fetchWithTimeout(`${apiBase()}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const raw = await parseJson(res);
  if (!res.ok) {
    throwApiError(raw, res.status);
  }
  const data = unwrapApi<{
    accessToken?: string;
    access_token?: string;
    user?: {
      email: string;
      name?: string;
      role: string;
      tenantId: string;
    };
    tenant?: { id: string; name: string };
  }>(raw);
  const token = data.accessToken ?? data.access_token;
  if (!token) throw new Error("register_failed");
  setCloudToken(token);
  setSessionPassword(body.password);
  return {
    access_token: token,
    user: data.user || null,
    tenant: data.tenant || null,
  };
}

export async function cloudForgotPassword(email: string) {
  const res = await fetchWithTimeout(`${apiBase()}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  return unwrapApi<{
    ok: boolean;
    message: string;
    expiresInSec: number;
    mailDelivered: boolean;
    devCode?: string;
    devToken?: string;
  }>(raw);
}

export async function cloudResetPassword(body: {
  email?: string;
  code?: string;
  token?: string;
  password: string;
}) {
  const res = await fetchWithTimeout(`${apiBase()}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  return unwrapApi<{ ok: boolean; message: string }>(raw);
}

/** Bearer token for cloud writes; re-login before health gate (health can false-negative). */
export async function ensureCloudSession(
  email?: string | null,
): Promise<{ ok: true } | { ok: false; message: string }> {
  if (getCloudToken()) return { ok: true };

  if (email) {
    const savedPass = getSessionPassword();
    // Only retry with the password captured at last successful login — never hardcode.
    if (savedPass) {
      try {
        await cloudLogin(email, savedPass);
        return { ok: true };
      } catch {
        /* fall through */
      }
    }
  }

  const healthy = await cloudHealth();
  if (healthy) {
    return {
      ok: false,
      message: email
        ? "登录已失效，请重新登录控制台"
        : "请先登录云枢控制台",
    };
  }
  return { ok: false, message: "无法连接云端 API，请检查网络后重试" };
}

export type ApiProject = {
  id: string;
  name: string;
  customer: string;
  phone: string;
  address?: string;
  city?: string;
  contact?: string;
  deliveryStatus: string;
  delivery_status?: string;
  category?: string;
  accessChannel?: string;
  vendorLabel?: string | null;
  deliveredAt?: string | null;
  gatewayId: string;
  agentStatus: string;
  lastPing: string;
  agent: string;
  core: string;
  os?: string;
  supervisor?: string;
  installForm: string;
  /** SSH 诊断旁路：unsupported|missing|stopped|enabled|unknown */
  sshBypassStatus?: string;
  sshBypassDetail?: string | null;
  cpu?: number | null;
  ramUsed?: number | null;
  ramTotal?: number | null;
  disk?: number | null;
  memoryPercent?: number | null;
  hostMetricsSupported?: boolean | null;
  hostMetricsSource?: string | null;
  hostMetricsUpdatedAt?: string | null;
  auth?: {
    scopes: string[];
    expires: string;
    status: string;
  };
  openAlerts?: number;
  /** 主机激活码（客户/集成商认领主机；Agent 在 HA 生成） */
  claimCode?: string | null;
  source?: string;
  tenantId?: string; // 租户 ID
  tenantName?: string; // 租户名称（集成商名称）
};

export type ClaimBody = {
  bindMode: "phone" | "code";
  phone?: string;
  activationCode?: string;
  projectName: string;
  customerName: string;
  /** 认领已有客户（未绑定主机）时传入，避免重复建档 */
  projectId?: string;
};

function authStatus(
  s?: string,
): Project["auth"]["status"] {
  if (s === "有效") return "有效";
  if (s === "已过期") return "已过期";
  if (s === "已暂停") return "已暂停";
  return "未授权";
}

/** API 有时返回 scopes 为 JSON 字符串（如 "[]"），统一为数组 */
function normalizeAuthScopes(raw: unknown): AuthScope[] {
  if (Array.isArray(raw)) {
    return raw.filter((s): s is AuthScope => typeof s === "string") as AuthScope[];
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter((s): s is AuthScope => typeof s === "string") as AuthScope[];
      }
    } catch {
      /* ignore */
    }
  }
  return [];
}

/** Map cloud project DTO → console Project */
export function mapApiProject(p: ApiProject, prev?: Partial<Project>): Project {
  const installForm =
    p.installForm === "Container"
      ? "Container"
      : p.installForm === "Supervised"
        ? "Supervised"
        : "HAOS";
  return {
    id: p.id,
    name: p.name,
    customer: p.customer,
    address: p.address || prev?.address || "待补充",
    city: p.city || prev?.city || "—",
    contact: p.contact || p.customer,
    phone: p.phone || "—",
    deliveryStatus: (p.deliveryStatus as DeliveryStatus) || "调试中",
    category: normalizeProjectCategory(p.category, p.deliveryStatus || p.delivery_status || ""),
    accessChannel: normalizeProjectAccessChannel(
      p.accessChannel ?? prev?.accessChannel,
    ),
    vendorLabel:
      p.vendorLabel !== undefined
        ? p.vendorLabel
        : (prev?.vendorLabel ?? null),
    deliveredAt: p.deliveredAt || prev?.deliveredAt,
    deviceCount: prev?.deviceCount ?? 0,
    onlineRate: prev?.onlineRate ?? 100,
    openAlerts: p.openAlerts ?? prev?.openAlerts ?? 0,
    openTickets: prev?.openTickets ?? 0,
    hardware: prev?.hardware || "待同步",
    installForm,
    supportLevel: prev?.supportLevel || "正式支持",
    core: p.core || prev?.core || "待同步",
    os: p.os || prev?.os || "待同步",
    supervisor: p.supervisor || prev?.supervisor || "待同步",
    agent: p.agent || prev?.agent || "待同步",
    agentStatus: (p.agentStatus as Project["agentStatus"]) || "已断开",
    lastPing: p.lastPing || "—",
    sshBypassStatus: p.sshBypassStatus || prev?.sshBypassStatus || "unknown",
    sshBypassDetail: p.sshBypassDetail ?? prev?.sshBypassDetail ?? null,
    cpu: p.cpu ?? prev?.cpu,
    ramUsed: p.ramUsed ?? prev?.ramUsed,
    ramTotal: p.ramTotal ?? prev?.ramTotal,
    disk: p.disk ?? prev?.disk,
    memoryPercent: p.memoryPercent ?? prev?.memoryPercent,
    hostMetricsSupported:
      p.hostMetricsSupported ?? prev?.hostMetricsSupported,
    hostMetricsSource: p.hostMetricsSource ?? prev?.hostMetricsSource,
    hostMetricsUpdatedAt:
      p.hostMetricsUpdatedAt ?? prev?.hostMetricsUpdatedAt,
    capabilities: (() => {
      const base = prev?.capabilities || capabilitiesForForm(installForm);
      if (p.hostMetricsSupported === false) {
        return base.map((c) =>
          c.key === "hostMetrics"
            ? {
                ...c,
                level: "blocked" as const,
                note: "当前主机无法可靠读取资源指标",
              }
            : c,
        );
      }
      if (p.hostMetricsSupported === true) {
        return base.map((c) =>
          c.key === "hostMetrics"
            ? {
                ...c,
                level: "supported" as const,
                note:
                  p.hostMetricsSource === "proc"
                    ? "来自容器可见资源（可能非宿主机全量）"
                    : undefined,
              }
            : c,
        );
      }
      return base;
    })(),
    auth: {
      scopes: normalizeAuthScopes(p.auth?.scopes ?? prev?.auth?.scopes),
      expires: p.auth?.expires || prev?.auth?.expires || "—",
      status: authStatus(p.auth?.status ?? prev?.auth?.status),
    },
    gatewayId: p.gatewayId || prev?.gatewayId || "—",
    errorCount: prev?.errorCount ?? 0,
    warningCount: prev?.warningCount ?? 0,
    agentKeyStatus: prev?.agentKeyStatus || "active",
    claimCode:
      (p.claimCode != null && String(p.claimCode).trim()) ||
      prev?.claimCode,
    master: prev?.master,
    tenantId: p.tenantId || prev?.tenantId,
    tenantName: p.tenantName || prev?.tenantName,
  };
}

async function authedFetch(path: string, init: RequestInit = {}) {
  const token = getCloudToken();
  if (!token) {
    throw Object.assign(new Error("no_token"), { code: "no_token" });
  }
  const res = await fetch(`${apiBase()}${path}`, {
    ...init,
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...opsTenantHeaders(path),
      ...(init.headers || {}),
    },
  });
  const raw = await parseJson(res);
  if (!res.ok) {
    if (res.status === 401) {
      // 仅凭证本身失效才清登录；forbidden_role / 缺租户等 401 不得踢出
      const msg = String(
        (raw as Record<string, unknown>)?.message ?? "",
      ).toLowerCase();
      const sessionDead =
        msg === "auth_required" ||
        msg === "invalid_token" ||
        msg === "token_expired" ||
        msg.includes("jwt expired") ||
        msg.includes("invalid signature");
      if (sessionDead) {
        clearCloudAuth();
      }
    }
    throwApiError(raw, res.status);
  }
  return unwrapApi(raw);
}

export async function cloudClaim(body: ClaimBody) {
  const data = await authedFetch("/projects/claim", {
    method: "POST",
    body: JSON.stringify({
      bind_mode: body.bindMode,
      phone: body.phone,
      activation_code: body.activationCode,
      project_name: body.projectName,
      customer_name: body.customerName,
      project_id: body.projectId,
    }),
  });
  return data as { project: ApiProject; gateway_id: string };
}

export async function cloudListProjects() {
  const token = getCloudToken();
  const res = await fetch(`${apiBase()}/projects`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const raw = await parseJson(res);
  if (!res.ok) {
    throwApiError(raw, res.status);
  }
  return unwrapApi(raw) as { items: ApiProject[] };
}

export type CreateProjectBody = {
  name: string;
  customerName?: string;
  phone?: string;
  address?: string;
  contact?: string;
  city?: string;
  accessChannel?: "ha" | "vendor" | "hybrid";
  vendorLabel?: string | null;
};

export async function cloudCreateProject(body: CreateProjectBody) {
  const data = await authedFetch("/projects", {
    method: "POST",
    body: JSON.stringify({
      name: body.name,
      customer_name: body.customerName,
      phone: body.phone,
      address: body.address,
      contact: body.contact,
      city: body.city,
      access_channel: body.accessChannel,
      vendor_label: body.vendorLabel,
    }),
  });
  return data as { project: ApiProject };
}

export type UpdateProjectBody = {
  name?: string;
  customerName?: string;
  phone?: string;
  address?: string;
  contact?: string;
  city?: string;
  accessChannel?: "ha" | "vendor" | "hybrid";
  vendorLabel?: string | null;
};

export async function cloudUpdateProject(
  projectId: string,
  body: UpdateProjectBody,
) {
  const data = await authedFetch(`/projects/${projectId}`, {
    method: "PATCH",
    body: JSON.stringify({
      name: body.name,
      customer_name: body.customerName,
      phone: body.phone,
      address: body.address,
      contact: body.contact,
      city: body.city,
      access_channel: body.accessChannel,
      vendor_label: body.vendorLabel,
    }),
  });
  return data as { project: ApiProject };
}

export type BillingSubscriptionDto = {
  plan: string;
  label: string;
  status: string;
  maxProjects: number;
  maxDevices: number;
};

export type BillingUsageDto = {
  projects: { current: number; limit: number; percentage: number };
  devices: { current: number; limit: number; percentage: number };
};

export async function cloudGetBillingSubscription() {
  return (await authedFetch("/billing/subscription")) as BillingSubscriptionDto;
}

export async function cloudGetBillingUsage() {
  return (await authedFetch("/billing/usage")) as BillingUsageDto;
}

export async function cloudListBillingPlans() {
  const data = await authedFetch("/billing/plans");
  return (Array.isArray(data) ? data : (data as { items?: unknown[] }).items ?? data) as Array<{
    id: string;
    label: string;
    maxProjects: number;
    maxDevices: number;
  }>;
}

export async function cloudUpdateBillingSubscription(plan: string) {
  return authedFetch("/billing/subscription", {
    method: "POST",
    body: JSON.stringify({ plan }),
  });
}

export type TenantInfoDto = {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export async function cloudGetTenantInfo() {
  return (await authedFetch("/settings/tenant")) as TenantInfoDto;
}

export type ApiKeyDto = {
  id: string;
  name: string;
  prefix: string;
  enabled: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
};

export async function cloudListApiKeys() {
  const data = await authedFetch("/settings/api-keys");
  return (data as { items: ApiKeyDto[] }).items;
}

export async function cloudChangePassword(
  currentPassword: string,
  newPassword: string,
) {
  return authedFetch("/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function cloudWechatUnbind(password: string) {
  return (await authedFetch("/auth/wechat/unbind", {
    method: "POST",
    body: JSON.stringify({ password }),
  })) as { ok: boolean; message?: string };
}

export type RoleDto = {
  id: string;
  name: string;
  description: string;
  builtin: boolean;
  permissions: string[];
};

export async function cloudListRoles() {
  const data = await authedFetch("/roles");
  return (data as { items: RoleDto[] }).items;
}

export async function cloudUpsertRole(role: {
  id?: string;
  name: string;
  description?: string;
  permissions: string[];
}) {
  if (role.id) {
    const data = await authedFetch(`/roles/${encodeURIComponent(role.id)}`, {
      method: "PUT",
      body: JSON.stringify(role),
    });
    return (data as { role: RoleDto }).role;
  }
  const data = await authedFetch("/roles", {
    method: "POST",
    body: JSON.stringify(role),
  });
  return (data as { role: RoleDto }).role;
}

export async function cloudDeleteRole(id: string) {
  return authedFetch(`/roles/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export type AuthRecordDto = {
  id: string;
  project: string;
  customer: string;
  at: string;
  scopes: string;
  status: string;
  source: string;
};

export async function cloudListAuthRecords(limit = 100) {
  const data = await authedFetch(`/auth-records?limit=${limit}`);
  return (data as { items: AuthRecordDto[] }).items;
}

export type HardwareAssetDto = {
  id: string;
  name: string;
  vendor: string;
  os: string;
  cpu: string;
  ram: string;
  storage: string;
  note: string;
  imageHint: string;
  fromCatalog: boolean;
  linkedProjectId?: string;
  linkedProjectName?: string;
};

export async function cloudListHardwareAssets() {
  const data = await authedFetch("/hardware-assets");
  return (data as { items: HardwareAssetDto[] }).items;
}

export async function cloudUpsertHardwareAsset(item: HardwareAssetDto) {
  if (item.id && !item.id.startsWith("tmp_")) {
    const data = await authedFetch(
      `/hardware-assets/${encodeURIComponent(item.id)}`,
      { method: "PUT", body: JSON.stringify(item) },
    );
    return (data as { item: HardwareAssetDto }).item;
  }
  const data = await authedFetch("/hardware-assets", {
    method: "POST",
    body: JSON.stringify(item),
  });
  return (data as { item: HardwareAssetDto }).item;
}

export async function cloudDeleteHardwareAsset(id: string) {
  return authedFetch(`/hardware-assets/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function cloudBindHardwareAsset(
  id: string,
  projectId: string,
  projectName: string,
) {
  const data = await authedFetch(
    `/hardware-assets/${encodeURIComponent(id)}/bind`,
    {
      method: "POST",
      body: JSON.stringify({ projectId, projectName }),
    },
  );
  return (data as { item: HardwareAssetDto }).item;
}

export async function cloudUnbindHardwareByProject(projectId: string) {
  return authedFetch(
    `/hardware-assets/unbind-project/${encodeURIComponent(projectId)}`,
    { method: "POST" },
  );
}

export async function cloudCreateApiKey(name: string) {
  return (await authedFetch("/settings/api-keys", {
    method: "POST",
    body: JSON.stringify({ name }),
  })) as { id: string; key: string; prefix: string };
}

export async function cloudRevokeApiKey(id: string) {
  return authedFetch(`/settings/api-keys/${id}`, { method: "DELETE" });
}

export type SystemConfigDto = {
  smtp: {
    host: string;
    port: number;
    secure: boolean;
    user: string;
    from: string;
    passSet: boolean;
    configured: boolean;
    source: "db" | "env" | "none";
  };
  appPublicUrl: string;
  hints: {
    host: string;
    port: number;
    user: string;
    from: string;
  };
};

export async function cloudGetSystemConfig() {
  return (await authedFetch("/settings/system")) as SystemConfigDto;
}

export async function cloudUpdateSystemConfig(body: {
  host?: string;
  port?: number;
  secure?: boolean;
  user?: string;
  from?: string;
  pass?: string;
  clearPass?: boolean;
  appPublicUrl?: string;
}) {
  return (await authedFetch("/settings/system", {
    method: "PUT",
    body: JSON.stringify(body),
  })) as SystemConfigDto;
}

export async function cloudTestSmtp(to?: string) {
  return (await authedFetch("/settings/system/smtp-test", {
    method: "POST",
    body: JSON.stringify({ to }),
  })) as { delivered: boolean; to: string };
}

export type MarketingRemoteDto = {
  pages: Record<string, unknown>;
  updatedAt: string | null;
};

export async function cloudGetMarketingPublic() {
  const res = await fetch(`${apiBase()}/public/marketing`, {
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
  });
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  return unwrapApi(raw) as MarketingRemoteDto;
}

export async function cloudGetMarketingAdmin() {
  return (await authedFetch("/settings/marketing")) as MarketingRemoteDto;
}

export async function cloudUpdateMarketing(body: {
  pages: Record<string, unknown>;
}) {
  return (await authedFetch("/settings/marketing", {
    method: "PUT",
    body: JSON.stringify(body),
  })) as MarketingRemoteDto;
}

export type NotificationDto = {
  id: string;
  type: string;
  title: string;
  body: string;
  level: string;
  projectId: string | null;
  read: boolean;
  readAt: string | null;
  createdAt: string;
};

export async function cloudListNotifications(opts?: {
  unreadOnly?: boolean;
  limit?: number;
}) {
  const q = new URLSearchParams();
  if (opts?.unreadOnly) q.set("unread_only", "true");
  if (opts?.limit) q.set("limit", String(opts.limit));
  const suffix = q.toString() ? `?${q}` : "";
  return (await authedFetch(`/notifications${suffix}`)) as {
    items: NotificationDto[];
    total: number;
    unread: number;
  };
}

export async function cloudMarkNotificationRead(notificationId?: string) {
  return authedFetch("/notifications/read", {
    method: "POST",
    body: JSON.stringify(
      notificationId ? { notification_id: notificationId } : {},
    ),
  });
}

export async function cloudMarkAllNotificationsRead() {
  return authedFetch("/notifications/read-all", { method: "POST" });
}

export async function cloudDeleteNotification(id: string) {
  return authedFetch(`/notifications/${id}`, { method: "DELETE" });
}

export type DashboardStatsDto = {
  projects: {
    total: number;
    delivered: number;
    online: number;
    offline: number;
    unbound?: number;
    notOnline?: number;
    onlineRate: number;
  };
  alerts: { active: number; critical: number; total: number };
  tickets: { open: number; today: number; total: number };
  devices: { total: number };
};

export async function cloudDashboardStats() {
  return (await authedFetch("/dashboard/stats")) as DashboardStatsDto;
}

export type DashboardActivityItem = {
  id: string;
  actor: string;
  action: string;
  target: string;
  result: string;
  category: string;
  at: string;
};

export async function cloudDashboardActivity(limit = 20) {
  const data = await authedFetch(`/dashboard/activity?limit=${limit}`);
  return (data as { items: DashboardActivityItem[] }).items;
}

export type ProjectHealthItem = {
  id: string;
  label: string;
  detail: string;
};

export type ProjectHealthFactor = {
  id: string;
  label: string;
  weight: number;
  score: number;
  penalty: number;
  detail: string;
  items: ProjectHealthItem[];
};

export type ProjectHealthDto = {
  score: number;
  level: string;
  summary: string;
  factors: ProjectHealthFactor[];
  computedAt: string;
};

export async function cloudGetProjectHealth(projectId: string) {
  return (await authedFetch(
    `/dashboard/projects/${encodeURIComponent(projectId)}/health`,
  )) as ProjectHealthDto;
}

export async function cloudListProjectHealth() {
  const data = await authedFetch("/dashboard/health");
  return (
    data as {
      items: Array<{
        projectId: string;
        score: number;
        level: string;
        summary: string;
        computedAt: string;
      }>;
    }
  ).items;
}

export type AdminTenantDto = {
  id: string;
  name: string;
  external_ref: string | null;
  status: string;
  uscc?: string | null;
  legal_name?: string | null;
  certification_status?: string;
  certification_submitted_at?: string | null;
  created_at: string;
  updated_at: string;
  stats: {
    projects: number;
    gateways: number;
    devices: number;
    active_alerts: number;
  };
};

export type TenantCertificationDto = {
  tenantId: string;
  tenantName: string;
  legalName: string | null;
  uscc: string | null;
  certificationStatus: string;
  certificationSubmittedAt: string | null;
  certificationReviewedAt: string | null;
  certificationNote: string | null;
  email: string;
  emailVerified: boolean;
  emailVerifiedAt: string | null;
  canSubmit: boolean;
};

export async function cloudGetTenantCertification() {
  return (await authedFetch("/tenant/certification")) as TenantCertificationDto;
}

export async function cloudSendCertificationEmailCode() {
  return (await authedFetch("/tenant/certification/email/send", {
    method: "POST",
    body: JSON.stringify({}),
  })) as {
    ok: boolean;
    alreadyVerified?: boolean;
    message: string;
    expiresInSec: number;
    mailDelivered: boolean;
    devCode?: string;
  };
}

export async function cloudVerifyCertificationEmail(code: string) {
  return (await authedFetch("/tenant/certification/email/verify", {
    method: "POST",
    body: JSON.stringify({ code }),
  })) as { ok: boolean; alreadyVerified?: boolean; message: string };
}

export async function cloudSubmitTenantCertification(body: {
  uscc: string;
  legalName: string;
}) {
  return (await authedFetch("/tenant/certification/submit", {
    method: "POST",
    body: JSON.stringify(body),
  })) as {
    ok: boolean;
    certificationStatus: string;
    message: string;
    uscc: string;
    legalName: string;
  };
}

export async function cloudAdminReviewTenantCertification(
  tenantId: string,
  body: { action: "approve" | "reject"; note?: string },
) {
  return authedFetch(`/admin/tenants/${tenantId}/certification`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export type PlatformStatsDto = {
  tenants: number;
  projects: number;
  gateways: number;
  online_gateways: number;
  devices: number;
  active_alerts: number;
  open_tickets: number;
  gateway_online_rate: number;
};

export async function cloudAdminListTenants() {
  const data = await authedFetch("/admin/tenants");
  return (data as { items: AdminTenantDto[]; total: number }).items;
}

export async function cloudAdminPlatformStats() {
  return (await authedFetch("/admin/platform-stats")) as PlatformStatsDto;
}

export async function cloudAdminCreateTenant(body: {
  name: string;
  externalRef?: string;
  adminEmail: string;
  adminName: string;
  adminPassword: string;
  plan?: string;
  maxProjects?: number;
  maxDevices?: number;
}) {
  return authedFetch("/admin/tenants", {
    method: "POST",
    body: JSON.stringify(body),
  }) as Promise<{
    id: string;
    name: string;
    status: string;
    message?: string;
    tenant?: { id: string; name: string };
    adminUser?: {
      email: string;
      name: string;
      role: string;
      tenantId: string;
    };
  }>;
}

/** Whether public integrator self-registration is open. */
export async function cloudGetRegisterStatus() {
  const res = await fetch(`${apiBase()}/auth/register-status`);
  const body = await res.json();
  const data =
    body && body.data !== undefined && body.message !== undefined
      ? body.data
      : body;
  if (!res.ok) throw new Error(data?.message || "register_status_failed");
  return data as { open: boolean };
}

export async function cloudAdminUpdateTenantStatus(id: string, status: string) {
  return authedFetch(`/admin/tenants/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

/** Delete an empty tenant (no projects). Protected tenants are rejected by API. */
export async function cloudAdminDeleteTenant(id: string) {
  return authedFetch(`/admin/tenants/${id}`, {
    method: "DELETE",
  }) as Promise<{ message?: string }>;
}

export type AdminTenantSubscriptionDto = {
  tenant: { id: string; name: string };
  subscription: {
    id?: string;
    plan: string;
    label: string;
    status: string;
    maxProjects: number;
    maxDevices: number;
    currentPeriodStart?: string | null;
    currentPeriodEnd?: string | null;
  };
  usage: {
    projects: { current: number; limit: number; percentage: number };
    devices: { current: number; limit: number; percentage: number };
  };
  plans: Array<{
    id: string;
    label: string;
    maxProjects: number;
    maxDevices: number;
  }>;
};

export async function cloudAdminGetTenantSubscription(tenantId: string) {
  return (await authedFetch(
    `/admin/tenants/${tenantId}/subscription`,
  )) as AdminTenantSubscriptionDto;
}

export async function cloudAdminSetTenantSubscription(
  tenantId: string,
  body: {
    plan?: string;
    status?: string;
    maxProjects?: number;
    maxDevices?: number;
  },
) {
  return (await authedFetch(`/admin/tenants/${tenantId}/subscription`, {
    method: "PUT",
    body: JSON.stringify(body),
  })) as AdminTenantSubscriptionDto;
}

export type AgentReleaseManifestDto = {
  version: string;
  sha256: string;
  size: number;
  published_at: string;
  path?: string;
};

export async function cloudAdminListAgentReleases() {
  return (await authedFetch("/admin/agent-releases")) as {
    latest: AgentReleaseManifestDto | null;
    items: AgentReleaseManifestDto[];
  };
}

export async function cloudAdminSetAgentReleaseLatest(version: string) {
  return (await authedFetch("/admin/agent-releases/latest", {
    method: "PUT",
    body: JSON.stringify({ version }),
  })) as { release: AgentReleaseManifestDto; notifiedTenants?: number };
}

export async function cloudAdminGetAqaraConfig() {
  return (await authedFetch("/admin/aqara-config")) as {
    config: AqaraAppConfigPublicDto | null;
    cryptoReady: boolean;
  };
}

export async function cloudAdminSetAqaraConfig(body: AqaraAppConfigDto) {
  return (await authedFetch("/admin/aqara-config", {
    method: "PUT",
    body: JSON.stringify(body),
  })) as { config: AqaraAppConfigPublicDto; cryptoReady: boolean };
}

export type WechatOpenConfigPublicDto = {
  appId: string;
  redirectUri: string;
  enabled: boolean;
  appSecretSet: boolean;
};

export async function cloudAdminGetWechatConfig() {
  return (await authedFetch("/admin/wechat-config")) as {
    config: WechatOpenConfigPublicDto | null;
    cryptoReady: boolean;
    configured: boolean;
  };
}

export async function cloudAdminSetWechatConfig(body: {
  appId: string;
  appSecret?: string;
  redirectUri?: string;
  enabled?: boolean;
}) {
  return (await authedFetch("/admin/wechat-config", {
    method: "PUT",
    body: JSON.stringify(body),
  })) as {
    config: WechatOpenConfigPublicDto;
    cryptoReady: boolean;
    configured: boolean;
  };
}

export async function cloudWechatLoginStatus() {
  const res = await fetchWithTimeout(`${apiBase()}/auth/wechat/status`);
  const raw = await parseJson(res);
  return unwrapApi<{
    configured: boolean;
    appId: string | null;
    redirectUri: string | null;
  }>(raw);
}

export async function cloudWechatCreateSession() {
  const res = await fetchWithTimeout(`${apiBase()}/auth/wechat/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
  if (!res.ok) {
    const raw = await parseJson(res).catch(() => null);
    const msg =
      (raw as { message?: string } | null)?.message || "无法创建微信登录会话";
    throw new Error(msg);
  }
  const raw = await parseJson(res);
  return unwrapApi<{
    sessionId: string;
    state: string;
    appId: string;
    redirectUri: string;
    authorizeUrl: string;
    expiresAt: string;
  }>(raw);
}

export async function cloudWechatPollSession(sessionId: string) {
  const res = await fetchWithTimeout(
    `${apiBase()}/auth/wechat/session/${encodeURIComponent(sessionId)}`,
  );
  const raw = await parseJson(res);
  return unwrapApi<{
    sessionId: string;
    status: "pending" | "confirmed" | "need_bind" | "expired" | "error";
    accessToken?: string;
    bindToken?: string | null;
    nickname?: string | null;
    message?: string;
    user?: {
      email: string;
      name?: string;
      role: string;
      tenantId: string;
      tenantName?: string;
      phoneBound?: boolean;
      wxBound?: boolean;
      tenant?: { id: string; name: string };
    } | null;
    tenant?: { id: string; name: string } | null;
  }>(raw);
}

export async function cloudWechatBind(body: {
  sessionId: string;
  bindToken: string;
  email: string;
  password: string;
}) {
  const res = await fetchWithTimeout(`${apiBase()}/auth/wechat/bind`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error("wechat_bind_failed");
  }
  const raw = await parseJson(res);
  const data = unwrapApi<{
    accessToken?: string;
    user?: {
      email: string;
      name?: string;
      role: string;
      tenantId: string;
      tenantName?: string;
    };
    tenant?: { id: string; name: string };
  }>(raw);
  const token = data.accessToken;
  if (!token) throw new Error("wechat_bind_failed");
  setCloudToken(token);
  return data;
}

/** Apply JWT from WeChat QR poll (no password). */
export function cloudApplyAccessToken(token: string) {
  setCloudToken(token);
  setSessionPassword(null);
}

export type IntegrationMirrorPackageDto = {
  domain: string;
  label?: string;
  upstream_repo?: string;
  asset?: string;
  latest?: string;
  version?: string;
  sha256?: string;
  size?: number;
  published_at?: string;
};

export type IntegrationMirrorManifestDto = {
  domain: string;
  label: string;
  upstream_repo: string;
  release_tag: string;
  version: string;
  asset: string;
  sha256: string;
  size: number;
  published_at: string;
};

export type HacsCatalogItemDto = {
  id: string;
  domain: string;
  full_name: string;
  name: string;
  description: string;
  last_version: string | null;
  stargazers_count: number;
  last_updated: string | null;
  in_mirror: boolean;
  mirror_latest?: string;
};

export async function cloudAdminListIntegrationMirrors() {
  return (await authedFetch("/admin/integration-mirrors")) as {
    catalog: { updated_at?: string; packages?: IntegrationMirrorPackageDto[] };
    packages: IntegrationMirrorPackageDto[];
    root?: string;
  };
}

export type DeviceCategoryMapDto = {
  id: string;
  match_kind: string;
  match_value: string;
  manufacturer: string | null;
  model: string | null;
  category: string | null;
  status: "mapped" | "pending";
  source: string;
  sample_name: string | null;
  sample_domain: string | null;
  device_count: number;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
};

export async function cloudListDeviceCategoryMaps() {
  return (await authedFetch("/device-category-maps")) as {
    items: Array<{
      match_kind: string;
      match_value: string;
      category: string;
      model: string | null;
    }>;
    categories: string[];
  };
}

export async function cloudAdminListDeviceCategoryMaps(opts?: {
  status?: "all" | "mapped" | "pending";
  q?: string;
}) {
  const qs = new URLSearchParams();
  if (opts?.status && opts.status !== "all") qs.set("status", opts.status);
  if (opts?.q) qs.set("q", opts.q);
  const suffix = qs.toString() ? `?${qs}` : "";
  return (await authedFetch(`/admin/device-category-maps${suffix}`)) as {
    items: DeviceCategoryMapDto[];
    stats: { total: number; mapped: number; pending: number };
  };
}

export async function cloudAdminSyncDeviceCategoryMaps() {
  return (await authedFetch("/admin/device-category-maps/sync", {
    method: "POST",
    body: "{}",
  })) as {
    scanned: number;
    inserted: number;
    updated: number;
    mapped: number;
    pending: number;
  };
}

export async function cloudAdminPatchDeviceCategoryMap(
  id: string,
  body: { category?: string; notes?: string | null },
) {
  return (await authedFetch(
    `/admin/device-category-maps/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  )) as DeviceCategoryMapDto;
}

export async function cloudAdminIngestIntegrationMirror(body: {
  domain: string;
  repo: string;
  asset?: string;
  label?: string;
}) {
  return (await authedFetch("/admin/integration-mirrors/ingest", {
    method: "POST",
    body: JSON.stringify(body),
  })) as {
    manifest: IntegrationMirrorManifestDto;
    catalog: { updated_at?: string; packages?: IntegrationMirrorPackageDto[] };
  };
}

export async function cloudAdminSearchHacsCatalog(opts?: {
  q?: string;
  limit?: number;
  offset?: number;
}) {
  const qs = new URLSearchParams();
  if (opts?.q) qs.set("q", opts.q);
  if (opts?.limit != null) qs.set("limit", String(opts.limit));
  if (opts?.offset != null) qs.set("offset", String(opts.offset));
  const suffix = qs.toString() ? `?${qs}` : "";
  return (await authedFetch(
    `/admin/integration-mirrors/hacs-catalog${suffix}`,
  )) as {
    total: number;
    offset: number;
    limit: number;
    items: HacsCatalogItemDto[];
    cached_at: string;
  };
}

export async function cloudAdminIngestHacsMirror(body: {
  full_name?: string;
  domain?: string;
  asset?: string;
  label?: string;
}) {
  return (await authedFetch("/admin/integration-mirrors/ingest-hacs", {
    method: "POST",
    body: JSON.stringify(body),
  })) as {
    manifest: IntegrationMirrorManifestDto;
    catalog: { updated_at?: string; packages?: IntegrationMirrorPackageDto[] };
  };
}

export type AiSleepDefaultsDto = {
  alias: string;
  start_at: string;
  intervene_above: number;
  setpoint: number;
  hvac_mode: string;
  fan_mode: string;
  temp_floor: number;
  temp_ceiling: number;
};

export type AiLogicClauseDto = {
  id: string;
  type: string;
  summary: string;
  params?: Record<string, string | number | boolean>;
};

export type AiAutomationLogicDto = {
  if: AiLogicClauseDto[];
  and: AiLogicClauseDto[];
  then: AiLogicClauseDto[];
};

export type AiAutomationRuleTemplateDto = {
  id: string;
  label: string;
  description: string;
  implemented: boolean;
  enabled: boolean;
  logic?: AiAutomationLogicDto;
  defaults?: AiSleepDefaultsDto;
  yaml_skeleton?: string;
  notes?: string;
};

export type AiAlgorithmTemplateDto = {
  id: string;
  label: string;
  description: string;
  channel: "packages" | "call_service" | string;
  published: boolean;
  open_via: string;
  automations?: AiAutomationRuleTemplateDto[];
  notes?: string;
  updated_at?: string | null;
  updated_by?: string | null;
};

export type AiCapabilityDto = {
  enabled: boolean;
  engine: string;
  engine_label: string;
  updated_at?: string | null;
  updated_by?: string | null;
  placement?: string;
  skills?: Array<{ id: string; label: string; description: string }>;
  algorithms?: AiAlgorithmTemplateDto[];
  ecliptic_control_points?: AiEclipticControlPointDto[];
  /** @deprecated use ecliptic_control_points */
  solar_terms?: AiSolarTermRowDto[];
  climate_zones?: AiClimateZoneOffsetDto[];
  coastal_offsets?: AiCoastalOffsetDto[];
  factor_policy?: AiClimateFactorPolicyDto;
  factor_model?: {
    model?: string;
    categories?: string[];
    by_category?: Record<string, string[]>;
    soft?: string[];
    hard_indoor?: string[];
    hard_outdoor?: string[];
    layers: string[];
    priority?: string;
    doc: string;
  };
  setpoint_pipeline?: string;
};

export type AiEclipticControlPointDto = {
  id: string;
  longitude_deg: number;
  label: string;
  hemisphere: "north" | "south";
  setpoint: number;
  intervene_above: number;
  intervene_below: number;
  temp_hysteresis: number;
  setpoint_offset: number;
  humidity_bias: number;
  dehumid_trigger: number;
  fresh_air_weight: number;
  compressor_min_run: number;
  vent_shade_coeff: number;
  notes?: string;
};

export type AiSolarTermRowDto = {
  id: string;
  name: string;
  setpoint: number;
  intervene_above: number;
  intervene_below: number;
  temp_hysteresis: number;
  setpoint_offset: number;
  humidity_bias: number;
  dehumid_trigger: number;
  fresh_air_weight: number;
  compressor_min_run: number;
  vent_shade_coeff: number;
  notes?: string;
};

export type AiClimateZoneOffsetDto = {
  id: string;
  label: string;
  setpoint_delta: number;
  humidity_bias_delta: number;
  dehumid_trigger_delta: number;
  fresh_air_weight_delta: number;
  notes?: string;
};

export type AiCoastalOffsetDto = {
  id: "near_sea" | "semi_inland" | "deep_inland" | string;
  label: string;
  setpoint_delta: number;
  humidity_bias_delta: number;
  dehumid_trigger_delta: number;
  fresh_air_weight_delta: number;
  notes?: string;
};

export type AiFactorCategoryDto =
  | "weather"
  | "indoor_air"
  | "house"
  | "behavior"
  | "strategy"
  | "ops";

export type AiFactorCatalogItemDto = {
  key: string;
  category: AiFactorCategoryDto;
  label: string;
  layer: string;
  severity: "blocking" | "recommended" | "optional";
  enabled: boolean;
  can_veto: boolean;
  suggest: string;
};

export type AiHardFactorItemDto = {
  key: string;
  group: string;
  label: string;
  severity: "blocking" | "recommended" | "optional";
  enabled: boolean;
  suggest: string;
};

export type AiClimateFactorPolicyDto = {
  debounce_minutes: number;
  dewpoint_margin_c: number;
  extreme_hot_c: number;
  extreme_cold_c: number;
  haze_aqi_veto: number;
  uv_veto: number;
  consecutive_rain_days_veto: number;
  delta_t_max_c: number;
  delta_rh_max_c: number;
  human_suppress_minutes: number;
  require_project_delivered: boolean;
  weather_api_required: boolean;
  soft_sunrise_sunset: boolean;
  soft_solar_elevation: boolean;
  solar_term_smooth_days: number;
  user_algorithm_switch: "entitlement";
  allow_season_lock: boolean;
  tou_pricing_enabled: boolean;
  energy_cap_kw: number;
  /** 无人关「不太热」余量 ℃；默认 1.5 */
  unoccupied_off_slack_c: number;
  /** 回差下限 ℃；默认 0.2 */
  temp_hysteresis_min_c: number;
  fallback: {
    setpoint: number;
    intervene_above: number;
    intervene_below: number;
    fresh_air_weight: number;
    dehumid_trigger: number;
    temp_hysteresis: number;
  };
  catalog: AiFactorCatalogItemDto[];
  hard_factors?: AiHardFactorItemDto[];
};

export async function cloudAdminGetAiCapability() {
  return (await authedFetch("/admin/ai-capability")) as AiCapabilityDto;
}

/** 运营算法演算：跨租户项目列表 */
export async function cloudAdminListAiDebugProjects() {
  return (await authedFetch("/admin/ai-debug-projects")) as {
    items: Array<{
      id: string;
      name: string;
      customer: string;
      tenantId: string;
      tenantName: string;
      agentStatus: string | null;
      city: string | null;
      address: string | null;
    }>;
    total: number;
  };
}

/** 空间认知总览（平台运营 · doc 15） */
export type CognitiveOverviewDto = {
  windowDays: number;
  empty: boolean;
  summary: {
    projectsTotal: number;
    projectsConnected: number;
    events7d: number;
    falsePositiveRate: number;
    degradedCount: number;
    avgHealthScore: number | null;
    confidence: {
      confirmed: number;
      suspected: number;
      low: number;
    };
  };
  anomalies: Array<{
    projectId: string;
    projectName: string;
    tenantId: string;
    tenantName: string;
    kind: "degraded" | "high_false_positive" | "silent" | "low_health";
    label: string;
    healthScore: number | null;
    falsePositiveRate: number | null;
    events7d: number;
    degradeReason: string | null;
  }>;
  coverage: Array<{
    projectId: string;
    projectName: string;
    tenantId: string;
    tenantName: string;
    connected: boolean;
    coveragePct: number | null;
    healthScore: number | null;
    events7d: number;
    confidence: { confirmed: number; suspected: number; low: number };
    degraded: boolean;
    lastEventAt: string | null;
  }>;
};

export async function cloudAdminGetCognitiveOverview() {
  return (await authedFetch(
    "/admin/cognitive/overview",
  )) as CognitiveOverviewDto;
}

export type CognitiveFpClustersDto = {
  empty: boolean;
  totalCases: number;
  reasonCodes: Array<{ code: string; label: string }>;
  clusters: Array<{
    reasonCode: string;
    reasonLabel: string;
    eventType: string;
    count: number;
    projectCount: number;
    projects: Array<{
      projectId: string;
      projectName: string;
      tenantName: string;
      count: number;
    }>;
    latestAt: string | null;
  }>;
};

export async function cloudAdminGetCognitiveFalsePositives() {
  return (await authedFetch(
    "/admin/cognitive/false-positives",
  )) as CognitiveFpClustersDto;
}

export type CognitiveRulePacksDto = {
  items: Array<{
    id: string;
    code: string;
    name: string;
    status: string;
    isDefault: boolean;
    latestVersion: string | null;
    versionCount: number;
    versions: Array<{
      id: string;
      version: string;
      payload: unknown;
      changelog: string | null;
      createdBy: string | null;
      createdAt: string;
    }>;
    updatedAt: string;
  }>;
};

export async function cloudAdminListCognitiveRulePacks() {
  return (await authedFetch(
    "/admin/cognitive/rule-packs",
  )) as CognitiveRulePacksDto;
}

export async function cloudAdminPublishCognitiveRulePackVersion(body: {
  packCode?: string;
  version: string;
  payload?: Record<string, unknown>;
  changelog?: string;
}) {
  return (await authedFetch("/admin/cognitive/rule-packs/versions", {
    method: "POST",
    body: JSON.stringify(body),
  })) as CognitiveRulePacksDto;
}

export type CognitiveCostGatesDto = {
  platform: {
    id: string;
    monthlyWakeLimit: number | null;
    dailyEventLimit?: number | null;
    dailyWakeLimit?: number | null;
    warnPct: number;
    hardCapEnabled: boolean;
    notes: string | null;
    updatedAt: string;
  } | null;
  tenants: Array<{
    tenantId: string;
    tenantName: string;
    monthlyWakeLimit: number | null;
    dailyEventLimit?: number | null;
    dailyWakeLimit?: number | null;
    wakeUsedMonth: number;
    warnPct: number;
    hardCapEnabled: boolean;
    warnHit: boolean;
    override: boolean;
    notes: string | null;
  }>;
  note: string;
};

export async function cloudAdminGetCognitiveCostGates() {
  return (await authedFetch(
    "/admin/cognitive/cost-gates",
  )) as CognitiveCostGatesDto;
}

export async function cloudAdminUpsertCognitiveCostGate(body: {
  scopeKind?: "platform" | "tenant";
  scopeId?: string;
  monthlyWakeLimit?: number | null;
  dailyEventLimit?: number | null;
  dailyWakeLimit?: number | null;
  warnPct?: number;
  hardCapEnabled?: boolean;
  notes?: string | null;
}) {
  return (await authedFetch("/admin/cognitive/cost-gates", {
    method: "PUT",
    body: JSON.stringify(body),
  })) as CognitiveCostGatesDto;
}

export type CognitiveCostUsageDto = {
  projectId: string;
  tenantId: string;
  dayEvents: number;
  dayWake: number;
  monthWake: number;
  limits: {
    dailyEventLimit: number;
    dailyWakeLimit: number;
    monthlyWakeLimit: number | null;
    hardCapEnabled: boolean;
    warnPct: number;
  };
};

export async function cloudGetCognitiveCostUsage(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/cost-usage`,
  )) as CognitiveCostUsageDto;
}

export type CognitiveVersionMatrixDto = {
  empty: boolean;
  summary: {
    projectsTotal: number;
    projectsConnected: number;
    driftCount?: number;
  };
  items: Array<{
    projectId: string;
    projectName: string;
    tenantId: string;
    tenantName: string;
    connected: boolean;
    modelVersion: string | null;
    rulepackCode: string | null;
    rulepackVersion: string | null;
    sourceRegistryVersion: string | null;
    desiredModelVersion?: string | null;
    desiredRulepackCode?: string | null;
    desiredRulepackVersion?: string | null;
    paused?: boolean;
    drift?: boolean;
    updatedAt: string | null;
  }>;
};

export async function cloudAdminGetCognitiveVersionMatrix() {
  return (await authedFetch(
    "/admin/cognitive/version-matrix",
  )) as CognitiveVersionMatrixDto;
}

export type CognitiveModelPacksDto = {
  items: Array<{
    id: string;
    code: string;
    name: string;
    status: string;
    isDefault: boolean;
    latestVersion: string | null;
    versionCount: number;
    versions: Array<{
      id: string;
      version: string;
      artifactUrl: string;
      sha256: string;
      signature: string;
      hasSignature: boolean;
      sizeBytes: number | null;
      changelog: string | null;
      createdBy: string | null;
      createdAt: string;
    }>;
    updatedAt: string;
  }>;
  note: string;
};

export async function cloudAdminListCognitiveModelPacks() {
  return (await authedFetch(
    "/admin/cognitive/model-packs",
  )) as CognitiveModelPacksDto;
}

export async function cloudAdminPublishCognitiveModelPackVersion(body: {
  packCode?: string;
  version: string;
  artifactUrl: string;
  sha256?: string;
  sizeBytes?: number | null;
  changelog?: string | null;
}) {
  return (await authedFetch("/admin/cognitive/model-packs/versions", {
    method: "POST",
    body: JSON.stringify(body),
  })) as CognitiveModelPacksDto;
}

export type CognitiveBatchAssignResult = {
  updated: number;
  skipped: number;
  total: number;
  matrix: CognitiveVersionMatrixDto;
};

export async function cloudAdminBatchAssignCognitiveTargets(body: {
  projectIds: string[];
  desiredModelVersion?: string | null;
  desiredRulepackCode?: string | null;
  desiredRulepackVersion?: string | null;
  paused?: boolean;
}) {
  return (await authedFetch("/admin/cognitive/batch-assign", {
    method: "POST",
    body: JSON.stringify(body),
  })) as CognitiveBatchAssignResult;
}

export type CognitiveCostReportDto = {
  day: { events: number; wake: number };
  month: { wake: number };
  projects: Array<{
    projectId: string;
    projectName: string;
    tenantId: string;
    tenantName: string;
    dayEvents: number;
    dayWake: number;
    monthWake: number;
  }>;
  generatedAt: string;
};

export async function cloudAdminGetCognitiveCostReport() {
  return (await authedFetch(
    "/admin/cognitive/cost-report",
  )) as CognitiveCostReportDto;
}

/** 调试台 · 空间认知事件流 */
export type PerceptionEventDto = {
  eventId: string;
  projectId: string;
  sourceId: string;
  sourceKind: string;
  sourceClass: string | null;
  tsStart: string;
  tsEnd: string | null;
  type: string;
  confidenceTier: "confirmed" | "suspected" | "low" | string;
  location: unknown;
  attributes: unknown;
  modelVersion: string | null;
  rulepackVersion: string | null;
  sourceRegistryVersion: string | null;
  evidenceSummary: string | null;
  flags: unknown;
  judgment: string;
  judgmentReason: string | null;
  judgmentNote: string | null;
  judgedBy: string | null;
  judgedAt: string | null;
};

export type PerceptionEventsListDto = {
  view: string;
  windowDays: number;
  items: PerceptionEventDto[];
  total: number;
};

export type PerceptionSummaryDto = {
  windowDays: number;
  total: number;
  confidence: { confirmed: number; suspected: number; low: number };
  falsePositiveCount: number;
  byType: Array<{ eventType: string; count: number; lastAt: string | null }>;
  silentCandidates: Array<{
    eventType: string;
    count: number;
    lastAt: string | null;
  }>;
};

export async function cloudListPerceptionEvents(
  projectId: string,
  opts?: { view?: "live" | "summary" | "anomaly"; limit?: number },
) {
  const q = new URLSearchParams();
  if (opts?.view) q.set("view", opts.view);
  if (opts?.limit) q.set("limit", String(opts.limit));
  const suffix = q.toString() ? `?${q}` : "";
  return (await authedFetch(
    `/projects/${projectId}/perception/events${suffix}`,
  )) as PerceptionEventsListDto;
}

export async function cloudGetPerceptionSummary(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/perception/summary`,
  )) as PerceptionSummaryDto;
}

export async function cloudSeedPerceptionEvents(projectId: string) {
  return (await authedFetch(`/projects/${projectId}/perception/events/seed`, {
    method: "POST",
    body: JSON.stringify({}),
  })) as { inserted: number; skipped: number; total: number };
}

export async function cloudMarkPerceptionFalsePositive(
  projectId: string,
  eventId: string,
  body: { reasonCode: string; note?: string },
) {
  return (await authedFetch(
    `/projects/${projectId}/perception/events/${encodeURIComponent(eventId)}/false-positive`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  )) as { event: PerceptionEventDto; already: boolean };
}

export type CognitiveHealthDto = {
  projectId: string;
  projectName: string;
  windowDays: number;
  score: number;
  factors: Array<{
    id: string;
    label: string;
    raw: number;
    contribution: number;
    hint: string;
  }>;
  eventsTotal: number;
  falsePositiveCount: number;
  falsePositiveRate: number;
  coveragePct: number;
  activeRulepackVersion: string | null;
  lastEventAt: string | null;
  computedAt: string;
};

export async function cloudGetCognitiveHealth(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/health`,
  )) as CognitiveHealthDto;
}

export type PerceptionAskDto = {
  ok: boolean;
  projectId?: string;
  question: string;
  answer: string | null;
  confidence?: number;
  sourceId?: string;
  eventId?: string;
  area?: string | null;
  degraded?: boolean;
  reason?: string;
  hint?: string;
  eventAt?: string;
  computedAt?: string;
  intent?: string;
  count?: number;
  basedOn?: {
    sourceKind?: string;
    sourceId?: string;
    eventId?: string;
  } | null;
};

export async function cloudPerceptionAsk(
  projectId: string,
  body: { question?: string; area?: string },
) {
  return (await authedFetch(`/projects/${projectId}/perception/ask`, {
    method: "POST",
    body: JSON.stringify(body),
  })) as PerceptionAskDto;
}

export type StateInferRunDto = {
  projectId: string;
  scanned: number;
  inserted: number;
  skipped: number;
  total: number;
  areasActive: string[];
  message?: string;
  sample?: Array<{ type?: string; source_id?: string; summary?: string }>;
  fusion?: {
    inserted?: number;
    decisions?: Array<{ area?: string; rule?: string; summary?: string }>;
    message?: string;
  };
};

export async function cloudRunStateInfer(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/state-infer/run`,
    { method: "POST", body: JSON.stringify({}) },
  )) as StateInferRunDto;
}

export type ProjectRulepackListDto = {
  activeVersion: string | null;
  items: Array<{
    id: string;
    version: string;
    payload: Record<string, unknown>;
    changelog: string | null;
    status: string;
    rolledBackFrom: string | null;
    createdBy: string | null;
    createdAt: string;
  }>;
};

export async function cloudListProjectRulepacks(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/rulepack`,
  )) as ProjectRulepackListDto;
}

export async function cloudPublishProjectRulepack(
  projectId: string,
  body: {
    payload?: Record<string, unknown>;
    changelog?: string;
    version?: string;
  },
) {
  return (await authedFetch(`/projects/${projectId}/cognitive/rulepack`, {
    method: "POST",
    body: JSON.stringify(body),
  })) as ProjectRulepackListDto;
}

export async function cloudRollbackProjectRulepack(
  projectId: string,
  version: string,
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/rulepack/rollback`,
    {
      method: "POST",
      body: JSON.stringify({ version }),
    },
  )) as ProjectRulepackListDto;
}

export type RulepackRecommendationDto = {
  projectId: string;
  basis: { city: string | null; architectureFamily: string | null };
  items: Array<{
    fromProjectId: string;
    fromProjectName: string;
    version: string;
    payload: Record<string, unknown>;
    healthScore: number | null;
    matchScore: number;
    reasons: string[];
  }>;
};

export async function cloudRecommendProjectRulepacks(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/rulepack/recommendations`,
  )) as RulepackRecommendationDto;
}

export type SourceRegistryItemDto = {
  id: string;
  sourceId: string;
  sourceKind: string;
  sourceClass: string | null;
  area: string | null;
  entityId: string | null;
  deviceId: string | null;
  label: string | null;
  registryVersion: string;
  meta: unknown;
  updatedBy: string | null;
  updatedAt: string;
  createdAt: string;
};

export async function cloudListSourceRegistry(projectId: string) {
  return (await authedFetch(`/projects/${projectId}/cognitive/sources`)) as {
    projectId: string;
    items: SourceRegistryItemDto[];
  };
}

export async function cloudUpsertSourceRegistry(
  projectId: string,
  body: {
    sourceId: string;
    sourceKind: string;
    sourceClass?: string;
    area?: string;
    entityId?: string;
    deviceId?: string;
    label?: string;
    registryVersion?: string;
  },
) {
  return (await authedFetch(`/projects/${projectId}/cognitive/sources`, {
    method: "PUT",
    body: JSON.stringify(body),
  })) as { projectId: string; items: SourceRegistryItemDto[] };
}

export async function cloudDeleteSourceRegistry(
  projectId: string,
  sourceId: string,
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/sources/${encodeURIComponent(sourceId)}`,
    { method: "DELETE" },
  )) as { projectId: string; items: SourceRegistryItemDto[] };
}

export type SourceSyncHaDto = {
  projectId: string;
  scanned: number;
  matched: number;
  inserted: number;
  updated: number;
  byKind: Record<string, number>;
  items: SourceRegistryItemDto[];
};

export async function cloudSyncSourcesFromHa(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/sources/sync-ha`,
    { method: "POST", body: JSON.stringify({}) },
  )) as SourceSyncHaDto;
}

/** Wave C 身份 / 家庭记忆（默认关；无特征向量） */
export type IdentityConsentNoticeDto = {
  version: string;
  title: string;
  collect: string;
  store: string;
  retention: string;
  access: string;
  delete: string;
  minors: string;
  withdraw: string;
};

export type IdentitySettingsDto = {
  projectId: string;
  enabled: boolean;
  consentVersion: string | null;
  consentAckAt: string | null;
  withdrawnAt: string | null;
  consentNotice: IdentityConsentNoticeDto;
  message?: string;
};

export type HouseholdMemberDto = {
  id: string;
  nickname: string;
  ageBand: string;
  consentStatus: string;
  consentAt: string | null;
  localEnrollStatus: string;
  note: string | null;
  createdAt: string;
};

export type FamilyMemoryDto = {
  id: string;
  memberId: string | null;
  kind: string;
  key: string;
  value: string;
  createdAt: string;
};

export async function cloudGetIdentitySettings(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/identity`,
  )) as IdentitySettingsDto;
}

export async function cloudSetIdentityEnabled(
  projectId: string,
  body: { enabled: boolean; ackConsent?: boolean },
) {
  return (await authedFetch(`/projects/${projectId}/cognitive/identity`, {
    method: "PUT",
    body: JSON.stringify(body),
  })) as IdentitySettingsDto;
}

export async function cloudListIdentityMembers(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/identity/members`,
  )) as { items: HouseholdMemberDto[] };
}

export async function cloudAddIdentityMember(
  projectId: string,
  body: {
    nickname: string;
    ageBand: "adult";
    ackConsent: boolean;
    note?: string;
  },
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/identity/members`,
    { method: "POST", body: JSON.stringify(body) },
  )) as { id: string; nickname: string; localEnrollStatus: string };
}

export async function cloudDeleteIdentityMember(
  projectId: string,
  memberId: string,
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/identity/members/${encodeURIComponent(memberId)}`,
    { method: "DELETE" },
  )) as { deleted: boolean; message?: string };
}

export async function cloudSetIdentityEnrollStatus(
  projectId: string,
  memberId: string,
  localEnrollStatus: "enrolled" | "pending" | "none",
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/identity/members/${encodeURIComponent(memberId)}/enroll-status`,
    {
      method: "PATCH",
      body: JSON.stringify({ localEnrollStatus }),
    },
  )) as { id: string; localEnrollStatus: string; message?: string };
}

export async function cloudListFamilyMemories(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/identity/memories`,
  )) as { items: FamilyMemoryDto[] };
}

export async function cloudAddFamilyMemory(
  projectId: string,
  body: { kind?: string; key: string; value: string; memberId?: string },
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/identity/memories`,
    { method: "POST", body: JSON.stringify(body) },
  )) as { id: string };
}

export async function cloudDeleteFamilyMemory(
  projectId: string,
  memoryId: string,
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/identity/memories/${encodeURIComponent(memoryId)}`,
    { method: "DELETE" },
  )) as { deleted: boolean };
}

/** Wave C 主动提醒 */
export type ReminderSettingsDto = {
  projectId: string;
  enabled: boolean;
  message?: string;
};

export type ReminderRuleDto = {
  id: string;
  name: string;
  enabled: boolean;
  triggerEventType: string;
  area: string | null;
  memberId: string | null;
  memoryKey: string | null;
  messageTemplate: string;
  cooldownSec: number;
  channel: string;
  createdAt: string;
};

export type ReminderLogDto = {
  id: string;
  ruleId: string;
  eventId: string | null;
  status: string;
  title: string | null;
  body: string | null;
  createdAt: string;
};

export async function cloudGetReminderSettings(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/reminders`,
  )) as ReminderSettingsDto;
}

export async function cloudSetReminderEnabled(
  projectId: string,
  enabled: boolean,
) {
  return (await authedFetch(`/projects/${projectId}/cognitive/reminders`, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  })) as ReminderSettingsDto;
}

export async function cloudListReminderRules(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/reminders/rules`,
  )) as { items: ReminderRuleDto[] };
}

export async function cloudCreateReminderRule(
  projectId: string,
  body: {
    name: string;
    triggerEventType: string;
    messageTemplate: string;
    area?: string;
    memberId?: string;
    memoryKey?: string;
    cooldownSec?: number;
  },
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/reminders/rules`,
    { method: "POST", body: JSON.stringify(body) },
  )) as { id: string };
}

export async function cloudDeleteReminderRule(
  projectId: string,
  ruleId: string,
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/reminders/rules/${encodeURIComponent(ruleId)}`,
    { method: "DELETE" },
  )) as { deleted: boolean };
}

export async function cloudListReminderLogs(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/reminders/logs`,
  )) as { items: ReminderLogDto[] };
}

export type SenseChannelDto = {
  id: string;
  label: string;
  level: "missing" | "dull" | "normal" | string;
  evidence: string;
  suggestion: string | null;
};

export type SenseCheckReportDto = {
  id: string;
  projectId: string;
  status: string;
  channels: SenseChannelDto[];
  healthScore: number | null;
  summary: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UpsellDraftDto = {
  id: string;
  projectId: string;
  reportId: string | null;
  status: string;
  title: string;
  body: string;
  channelGaps: unknown;
  aiDraft: boolean;
  createdBy: string | null;
  confirmedBy: string | null;
  confirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function cloudListSenseChecks(projectId: string) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/sense-checks`,
  )) as { items: SenseCheckReportDto[] };
}

export async function cloudGenerateSenseCheck(
  projectId: string,
  notes?: string,
) {
  return (await authedFetch(`/projects/${projectId}/cognitive/sense-checks`, {
    method: "POST",
    body: JSON.stringify({ notes }),
  })) as SenseCheckReportDto;
}

export async function cloudPatchSenseCheck(
  projectId: string,
  reportId: string,
  body: { notes?: string; status?: string },
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/sense-checks/${encodeURIComponent(reportId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  )) as SenseCheckReportDto;
}

export async function cloudListUpsellDrafts(projectId: string) {
  return (await authedFetch(`/projects/${projectId}/cognitive/upsells`)) as {
    items: UpsellDraftDto[];
  };
}

export async function cloudGenerateUpsellFromSenseCheck(
  projectId: string,
  reportId: string,
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/sense-checks/${encodeURIComponent(reportId)}/upsell`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  )) as UpsellDraftDto;
}

export async function cloudConfirmUpsellDraft(
  projectId: string,
  draftId: string,
  body?: { title?: string; body?: string },
) {
  return (await authedFetch(
    `/projects/${projectId}/cognitive/upsells/${encodeURIComponent(draftId)}/confirm`,
    {
      method: "POST",
      body: JSON.stringify(body || {}),
    },
  )) as UpsellDraftDto;
}

export async function cloudAdminSetAiCapability(enabled: boolean) {
  return (await authedFetch("/admin/ai-capability", {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  })) as AiCapabilityDto;
}

export async function cloudAdminSetSolarTerms(items: AiSolarTermRowDto[]) {
  return (await authedFetch("/admin/ai-climate/solar-terms", {
    method: "PUT",
    body: JSON.stringify({ items }),
  })) as AiSolarTermRowDto[];
}

export async function cloudAdminSetEclipticPoints(
  items: AiEclipticControlPointDto[],
) {
  return (await authedFetch("/admin/ai-climate/ecliptic-points", {
    method: "PUT",
    body: JSON.stringify({ items }),
  })) as AiEclipticControlPointDto[];
}

export async function cloudAdminSetClimateZones(items: AiClimateZoneOffsetDto[]) {
  return (await authedFetch("/admin/ai-climate/zones", {
    method: "PUT",
    body: JSON.stringify({ items }),
  })) as AiClimateZoneOffsetDto[];
}

export async function cloudAdminSetCoastalOffsets(items: AiCoastalOffsetDto[]) {
  return (await authedFetch("/admin/ai-climate/coastal-offsets", {
    method: "PUT",
    body: JSON.stringify({ items }),
  })) as AiCoastalOffsetDto[];
}

export async function cloudAdminSetFactorPolicy(
  body: Partial<AiClimateFactorPolicyDto>,
) {
  return (await authedFetch("/admin/ai-climate/factor-policy", {
    method: "PUT",
    body: JSON.stringify(body),
  })) as AiClimateFactorPolicyDto;
}

export type AiClimateRuntimeSettingsDto = {
  runtime_enabled?: boolean;
  scan_seconds?: number;
  refresh_minutes?: number;
  startup_quiet_seconds?: number;
};

export type AiClimateRuntimeView = {
  stored: AiClimateRuntimeSettingsDto;
  env_defaults: {
    runtime_enabled: boolean;
    scan_seconds: number;
    refresh_minutes: number;
    startup_quiet_seconds: number;
  };
  effective: {
    runtime_enabled: boolean;
    scan_seconds: number;
    refresh_minutes: number;
    startup_quiet_seconds: number;
  };
};

/** 平台 AI 温控运行参数（生效值 + env 兜底） */
export async function cloudAdminGetAiClimateRuntime() {
  return (await authedFetch("/admin/ai-climate/runtime")) as AiClimateRuntimeView;
}

/** 调整运行参数；scan_seconds=0 停用扫描，留空字段继承 env */
export async function cloudAdminSetAiClimateRuntime(
  body: AiClimateRuntimeSettingsDto,
) {
  return (await authedFetch("/admin/ai-climate/runtime", {
    method: "PUT",
    body: JSON.stringify(body),
  })) as AiClimateRuntimeView | { error?: string };
}

export type AiClimateSensorBindingsDto = {
  temp?: string | null;
  humidity?: string | null;
  occupancy?: string | null;
  doors?: string[];
};

/** 项目感知输入绑定（空 = 自动匹配） */
export async function cloudAdminGetSensorBindings(projectId: string) {
  return (await authedFetch(
    `/admin/ai-climate/sensor-bindings/${encodeURIComponent(projectId)}`,
  )) as { project_id: string; bindings: AiClimateSensorBindingsDto | null };
}

/** 设置/清除项目感知输入绑定；全空 = 清除（回自动匹配） */
export async function cloudAdminSetSensorBindings(
  projectId: string,
  body: AiClimateSensorBindingsDto,
) {
  return (await authedFetch(
    `/admin/ai-climate/sensor-bindings/${encodeURIComponent(projectId)}`,
    {
      method: "PUT",
      body: JSON.stringify(body),
    },
  )) as { project_id: string; bindings: AiClimateSensorBindingsDto | null };
}

export async function cloudAdminSetAiAlgorithmTemplate(
  algorithmId: string,
  body: {
    label?: string;
    description?: string;
    published?: boolean;
    open_via?: string;
    notes?: string;
    automations?: AiAutomationRuleTemplateDto[];
    automation_id?: string;
    automation?: Partial<AiAutomationRuleTemplateDto>;
  },
) {
  return (await authedFetch(`/admin/ai-algorithms/${algorithmId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  })) as AiAlgorithmTemplateDto;
}

export async function cloudAiStatus() {
  return (await authedFetch("/ai-center/status")) as AiCapabilityDto;
}

export async function cloudDeleteProject(
  projectId: string,
): Promise<{ ok: boolean; message?: string }> {
  try {
    await authedFetch(`/projects/${projectId}`, { method: "DELETE" });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: apiErrorMessage(err, "删除失败，请重试") };
  }
}

export type ProjectCategory = "not_delivered" | "pending_support" | "delivered";

export type ProjectAccessChannel = "ha" | "vendor" | "hybrid";

export const PROJECT_ACCESS_CHANNEL_LABELS: Record<ProjectAccessChannel, string> =
  {
    ha: "HA",
    vendor: "品牌",
    hybrid: "HA+品牌",
  };

export const PROJECT_VENDOR_BRANDS = ["Aqara"] as const;

export function normalizeProjectAccessChannel(
  raw: unknown,
): ProjectAccessChannel {
  const v = String(raw || "")
    .trim()
    .toLowerCase();
  if (v === "vendor" || v === "manufacturer" || v === "cloud") return "vendor";
  if (v === "hybrid" || v === "multi") return "hybrid";
  return "ha";
}

/** Examples: HA · Aqara · HA+Aqara */
export function formatProjectAccessLabel(
  channel?: string | null,
  vendorLabel?: string | null,
): string {
  const c = normalizeProjectAccessChannel(channel);
  const brand = String(vendorLabel || "").trim();
  if (c === "ha") return "HA";
  if (c === "vendor") return brand || "品牌";
  return brand ? `HA+${brand}` : "HA+品牌";
}

/** Whether HA Agent / host bind actions apply. */
export function projectSupportsHaHost(channel?: string | null): boolean {
  const c = normalizeProjectAccessChannel(channel);
  return c === "ha" || c === "hybrid";
}

/** Whether manufacturer OpenAPI (Pilot) applies. */
export function projectSupportsVendorApi(channel?: string | null): boolean {
  const c = normalizeProjectAccessChannel(channel);
  return c === "vendor" || c === "hybrid";
}

export const PROJECT_CATEGORY_LABELS: Record<ProjectCategory, string> = {
  not_delivered: "未交付",
  pending_support: "待售后",
  delivered: "已交付",
};

const LEGACY_CATEGORY_MAP: Record<string, ProjectCategory> = {
  debugging: "not_delivered",
  after_sales: "pending_support",
  archived: "delivered",
};

/** 从交付状态派生项目分类 */
export function deriveProjectCategory(deliveryStatus: string): ProjectCategory {
  const ds = deliveryStatus.trim();
  if (ds === "delivered" || ds === "已交付") {
    return "delivered";
  }
  if (
    ["auth_pending", "authorized", "auth_paused", "待授权", "已授权", "授权暂停"].includes(
      ds,
    )
  ) {
    return "pending_support";
  }
  return "not_delivered";
}

/** 归一化 API/DB category（含旧枚举迁移兼容） */
export function normalizeProjectCategory(
  category: string | undefined,
  deliveryStatus: string,
): ProjectCategory {
  if (category) {
    if (category in PROJECT_CATEGORY_LABELS) {
      return category as ProjectCategory;
    }
    const legacy = LEGACY_CATEGORY_MAP[category];
    if (legacy) return legacy;
  }
  return deriveProjectCategory(deliveryStatus);
}

export async function cloudBindHost(
  projectId: string,
  mode: "read" | "tell" = "tell",
  opts?: { forceNew?: boolean },
) {
  const data = await authedFetch(`/projects/${projectId}/bind-host`, {
    method: "POST",
    body: JSON.stringify({ mode, force_new: opts?.forceNew ?? false }),
  });
  return data as {
    mode: "read" | "tell";
    activation_id?: string;
    activation_key?: string;
    user_code?: string;
    qr_payload?: string;
    expires_in?: number;
    project_id: string;
    note?: string;
    reused?: boolean;
    items?: PendingActivation[];
    total?: number;
  };
}

export type PendingActivation = {
  activation_id: string;
  activation_key: string;
  user_code: string;
  setup_id?: string;
  qr_payload?: string;
  expires_in: number;
  poll_interval: number;
  key_version: string;
  project_id?: string;
  source?: string;
  created_at?: string;
};

/** 查询 Agent 侧待认领激活码（模拟读取主机标签） */
export async function cloudListPendingActivations(projectId?: string) {
  const params = projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
  const data = await authedFetch(`/agent/activations/pending${params}`);
  return data as { items: PendingActivation[]; total: number };
}

export async function cloudGetProject(id: string) {
  const token = getCloudToken();
  const res = await fetchWithTimeout(`${apiBase()}/projects/${encodeURIComponent(id)}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...opsTenantHeaders(),
    },
  });
  const raw = await parseJson(res);
  if (!res.ok) {
    throwApiError(raw, res.status);
  }
  return unwrapApi(raw) as { project: ApiProject };
}

export type ApiAuditLog = {
  id: string;
  time: string;
  day: string;
  actor: string;
  action: string;
  target: string;
  result: string;
  category: string;
  createdAt?: string;
  created_at?: string;
};

export async function cloudListAuditLogs() {
  const data = await authedFetch("/audit-logs");
  return data as { items: ApiAuditLog[] };
}

/** Platform-wide audit (cross-tenant); normalizes created_at → time/day. */
export async function cloudAdminListAuditLogs(limit = 200) {
  const data = (await authedFetch(
    `/admin/audit-all?limit=${Math.min(500, Math.max(1, limit))}`,
  )) as {
    items: Array<{
      id: string;
      actor: string;
      action: string;
      target: string;
      result: string;
      category: string;
      created_at: string;
    }>;
  };
  return {
    items: (data.items || []).map((l): ApiAuditLog => {
      const d = new Date(l.created_at);
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return {
        id: l.id,
        time: Number.isNaN(d.getTime()) ? "—" : `${hh}:${mm}`,
        day: Number.isNaN(d.getTime())
          ? ""
          : d.toISOString().slice(0, 10),
        actor: l.actor,
        action: l.action,
        target: l.target,
        result: l.result,
        category: l.category,
        createdAt: l.created_at,
      };
    }),
  };
}

export function mapApiAuditLog(l: ApiAuditLog): AuditEntry {
  return {
    id: l.id,
    time: l.time,
    day: l.day,
    actor: l.actor,
    action: l.action,
    target: l.target,
    result: l.result,
    category: l.category as AuditCategory,
    createdAt: l.createdAt || l.created_at,
  };
}

export type ApiUser = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  name: string;
  role: string;
  enabled: boolean;
  projects?: string[];
  projectIds?: string[];
  projectScope?: "all" | "assigned";
  lastActive?: string;
  createdAt?: string;
  updatedAt?: string;
};

export async function cloudListUsers() {
  const data = await authedFetch("/users");
  return data as { items: ApiUser[] };
}

export async function cloudRemoveUser(id: string): Promise<void> {
  await authedFetch(`/users/${id}`, { method: "DELETE" });
}

export async function cloudCreateUser(body: {
  email: string;
  name: string;
  password: string;
  role: string;
  projectIds?: string[];
}): Promise<ApiUser> {
  const data = await authedFetch("/users", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data as ApiUser;
}

export async function cloudUpdateUser(
  id: string,
  body: {
    name?: string;
    role?: string;
    enabled?: boolean;
    password?: string;
    projectIds?: string[];
  },
): Promise<void> {
  await authedFetch(`/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** 中文姓名：姓在前、名在后；API 仅返回合并 `name` 时用于编辑表单回填。 */
function splitDisplayName(name: string): { firstName: string; lastName: string } {
  const n = (name || "").trim();
  if (!n) return { firstName: "", lastName: "" };
  if (n.length === 1) return { firstName: n, lastName: "" };
  return { lastName: n.slice(0, 1), firstName: n.slice(1) };
}

export function mapApiUser(u: ApiUser): StaffUser {
  const names = Array.isArray(u.projects) ? u.projects : [];
  const scope = u.projectScope || (u.role === "integrator_admin" || u.role === "platform_admin" ? "all" : "assigned");
  const displayName = u.name || `${u.lastName || ""}${u.firstName || ""}`;
  const split = splitDisplayName(displayName);
  return {
    id: u.id,
    email: u.email,
    firstName: u.firstName || split.firstName,
    lastName: u.lastName || split.lastName,
    name: displayName,
    role: u.role,
    enabled: u.enabled,
    projectIds: u.projectIds || [],
    projectScope: scope,
    projects:
      scope === "all"
        ? "全部项目"
        : names.length
          ? names.join("、")
          : "未分配",
    lastActive: u.lastActive || "尚未登录",
  };
}

export async function cloudListProjectMembers(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/members`);
  return data as {
    items: Array<{
      id: string;
      userId: string;
      email: string;
      name: string;
      role: string;
      enabled: boolean;
      createdAt: string;
    }>;
  };
}

export async function cloudSetProjectMembers(
  projectId: string,
  userIds: string[],
): Promise<boolean> {
  try {
    await authedFetch(`/projects/${projectId}/members`, {
      method: "PUT",
      body: JSON.stringify({ userIds }),
    });
    return true;
  } catch {
    return false;
  }
}

export type ApiDeviceEntity = {
  id: string;
  projectId: string;
  gatewayId: string;
  deviceId?: string | null;
  entityId: string;
  name: string;
  room: string;
  domain: string;
  state: string;
  online: boolean;
  lastSeen: string;
  attributes: Record<string, string | number | boolean>;
  source: "HA" | "CLOUD";
  manufacturer?: string | null;
  model?: string | null;
  primaryEntityId?: string;
  entityCount?: number;
  areaId?: string | null;
  /** 云枢品类覆盖；空则按 domain 推断 */
  deviceCategory?: string | null;
  parentDeviceId?: string | null;
  viaDeviceId?: string | null;
  configEntryId?: string | null;
  swVersion?: string | null;
  isChild?: boolean;
  /** HA DeviceEntryType；service = 已集成 → 服务（非 call_service 目录） */
  entryType?: "device" | "service";
  entities?: ApiDeviceEntity[];
};

export async function cloudListProjectDevices(
  projectId: string,
  options: {
    limit?: number;
    offset?: number;
    domain?: string;
    online?: boolean;
  } = {},
) {
  const params = new URLSearchParams({
    limit: String(options.limit ?? 200),
    offset: String(options.offset ?? 0),
  });
  if (options.domain) params.set("domain", options.domain);
  if (options.online === true) params.set("online", "true");
  if (options.online === false) params.set("online", "false");
  const data = await authedFetch(
    `/projects/${projectId}/devices?${params.toString()}`,
  );
  return data as {
    items: ApiDeviceEntity[];
    total: number;
    onlineTotal?: number;
    offlineTotal?: number;
    limit: number;
    offset: number;
  };
}

/** Raw HA entities (incl. scene / automation) — not field-device filtered. */
export async function cloudListProjectEntities(
  projectId: string,
  options: { limit?: number; offset?: number; domain?: string } = {},
) {
  const params = new URLSearchParams({
    limit: String(options.limit ?? 200),
    offset: String(options.offset ?? 0),
  });
  if (options.domain) params.set("domain", options.domain);
  const data = await authedFetch(
    `/projects/${projectId}/entities?${params.toString()}`,
  );
  return data as {
    items: ApiDeviceEntity[];
    total: number;
    limit: number;
    offset: number;
  };
}

export async function cloudUpdateDeviceMeta(
  projectId: string,
  deviceId: string,
  body: {
    name?: string;
    room?: string;
    entityId?: string;
    /** 云枢品类覆盖；传 "" / "auto" 清除覆盖 */
    deviceCategory?: string | null;
  },
) {
  const data = await authedFetch(
    `/projects/${projectId}/devices/${encodeURIComponent(deviceId)}/meta`,
    {
      method: "PATCH",
      body: JSON.stringify({
        name: body.name,
        room: body.room,
        entity_id: body.entityId,
        device_category: body.deviceCategory,
      }),
    },
  );
  return data as {
    device: {
      id: string;
      name: string;
      room: string;
      deviceCategory?: string | null;
    };
    command_id?: string | null;
    ha_dispatched?: boolean;
  };
}

export type ApiFloorPlanLayout = {
  rooms: Array<{
    id: string;
    name: string;
    syncRoom?: string;
    syncRooms?: string[];
    floorId?: string;
    x: number;
    y: number;
    w: number;
    h: number;
  }>;
  floors?: Array<{ id: string; name: string }>;
  updatedAt?: string;
};

export async function cloudGetFloorPlan(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/floor-plan`);
  return data as {
    layout: ApiFloorPlanLayout | null;
    updatedAt: string | null;
  };
}

export async function cloudSaveFloorPlan(
  projectId: string,
  layout: ApiFloorPlanLayout,
) {
  const data = await authedFetch(`/projects/${projectId}/floor-plan`, {
    method: "POST",
    body: JSON.stringify({ layout }),
  });
  return data as { updatedAt: string };
}

export async function cloudRenameRoom(
  projectId: string,
  body: {
    oldName?: string;
    newName: string;
    oldNames?: string[];
  },
) {
  const data = await authedFetch(`/projects/${projectId}/rooms/rename`, {
    method: "POST",
    body: JSON.stringify({
      old_name: body.oldName,
      old_names: body.oldNames,
      new_name: body.newName,
    }),
  });
  return data as {
    oldName: string;
    oldNames?: string[];
    newName: string;
    area_id?: string | null;
    devicesUpdated: number;
    ha_area_renamed: boolean;
    ha_device_fallback?: number;
    ha_merged?: number;
    ha_dispatched: boolean;
    command_id?: string | null;
  };
}

export async function cloudCreateRoom(
  projectId: string,
  body: { name: string; floorName?: string },
) {
  const data = await authedFetch(`/projects/${projectId}/rooms/create`, {
    method: "POST",
    body: JSON.stringify({
      name: body.name,
      floor_name: body.floorName,
    }),
  });
  return data as {
    name: string;
    floor_name: string | null;
    existed: boolean;
    area_id?: string | null;
    ha_dispatched: boolean;
    command_id?: string | null;
  };
}

export async function cloudDeleteRoom(
  projectId: string,
  body: { name?: string; names?: string[] },
) {
  const data = await authedFetch(`/projects/${projectId}/rooms/delete`, {
    method: "POST",
    body: JSON.stringify({
      name: body.name,
      names: body.names,
    }),
  });
  return data as {
    names: string[];
    deleted: number;
    ha_dispatched: boolean;
    ha_commands?: number;
    command_ids?: string[];
  };
}

export async function cloudSyncFloor(
  projectId: string,
  body: {
    action: "floor_create" | "floor_delete" | "area_set_floor";
    floorName: string;
    areaName?: string;
  },
) {
  const data = await authedFetch(`/projects/${projectId}/floors/sync`, {
    method: "POST",
    body: JSON.stringify({
      action: body.action,
      floor_name: body.floorName,
      area_name: body.areaName,
    }),
  });
  return data as {
    action: string;
    floor_name: string | null;
    area_name: string | null;
    ha_dispatched: boolean;
    command_id?: string | null;
  };
}

/** 项目配置配方（开通向导）— 不含密钥 */
export type BootstrapRecipe = {
  version: 1;
  sourceProjectId?: string;
  capturedAt?: string;
  integrations: Array<{ domain: string; title?: string }>;
  areas: Array<{ name: string; floorName?: string }>;
  packages: Array<{
    kind: string;
    filename: string;
    name: string;
    content: string;
    sourceRevisionId?: string;
  }>;
  system?: {
    locationName?: string;
    timeZone?: string;
    language?: string;
  };
};

export async function cloudGetBootstrapRecipe(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/bootstrap-recipe`);
  return data as { recipe: BootstrapRecipe | null };
}

export async function cloudPutBootstrapRecipe(
  projectId: string,
  recipe: BootstrapRecipe,
) {
  const data = await authedFetch(`/projects/${projectId}/bootstrap-recipe`, {
    method: "PUT",
    body: JSON.stringify({ recipe }),
  });
  return data as { recipe: BootstrapRecipe };
}

export async function cloudCaptureBootstrapRecipe(
  projectId: string,
  body: {
    integrations?: Array<{ domain: string; title?: string }>;
  } = {},
) {
  const data = await authedFetch(
    `/projects/${projectId}/bootstrap-recipe/capture`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  return data as { recipe: BootstrapRecipe };
}

/** 开通向导步骤键 — 与 packages/contracts/src/bootstrap-wizard.ts 保持一致 */
export const HA_BOOTSTRAP_STEPS = [
  "bind",
  "areas",
  "packages",
  "integrations",
  "sync",
] as const;
export const AQARA_BOOTSTRAP_STEPS = [
  "authorize",
  "families",
  "devices",
  "scenes",
] as const;

export type BootstrapWizardKind = "ha" | "aqara";

export const BOOTSTRAP_WIZARD_STEPS: Record<
  BootstrapWizardKind,
  readonly string[]
> = {
  ha: HA_BOOTSTRAP_STEPS,
  aqara: AQARA_BOOTSTRAP_STEPS,
};

export type BootstrapWizardProgress = Partial<
  Record<BootstrapWizardKind, { done: string[]; completedAt?: string | null }>
>;

/** 该通道需要哪些向导；hybrid 两个都要。 */
export function bootstrapWizardsForChannel(
  channel?: string | null,
): BootstrapWizardKind[] {
  const c = normalizeProjectAccessChannel(channel);
  if (c === "vendor") return ["aqara"];
  if (c === "hybrid") return ["ha", "aqara"];
  return ["ha"];
}

export function isBootstrapWizardComplete(
  kind: BootstrapWizardKind,
  done: readonly string[],
): boolean {
  const set = new Set(done);
  return BOOTSTRAP_WIZARD_STEPS[kind].every((s) => set.has(s));
}

export async function cloudGetBootstrapProgress(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/bootstrap-progress`);
  return data as { progress: BootstrapWizardProgress };
}

export async function cloudPutBootstrapProgress(
  projectId: string,
  channel: BootstrapWizardKind,
  done: readonly string[],
) {
  const data = await authedFetch(`/projects/${projectId}/bootstrap-progress`, {
    method: "PUT",
    body: JSON.stringify({ channel, done }),
  });
  return data as { progress: BootstrapWizardProgress };
}

export type TransitionAction =
  | "handoff"
  | "request-auth"
  | "grant-auth"
  | "end-auth"
  | "withdraw-handoff";

export async function cloudTransition(
  projectId: string,
  action: TransitionAction,
  body: Record<string, unknown> = {},
) {
  const data = await authedFetch(`/projects/${projectId}/${action}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data as {
    project: ApiProject;
    ha_restart_dispatched?: boolean;
    owner_auth_url?: string;
    owner_auth_token?: string;
    owner_notify?: { smsDelivered?: boolean };
  };
}

export async function cloudCreateOwnerAuthLink(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/owner-auth-link`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  return data as {
    owner_auth_url: string;
    owner_auth_token?: string;
    owner_notify?: { smsDelivered?: boolean };
  };
}

export async function publicOwnerAuthPreview(token: string) {
  const res = await fetchWithTimeout(
    `${apiBase()}/public/owner-auth?token=${encodeURIComponent(token)}`,
  );
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  return unwrapApi(raw) as {
    project?: {
      id: string;
      name: string;
      customer: string;
      deliveryStatus: string;
      scopes: string[];
    };
    error?: string;
  };
}

export async function publicOwnerAuthGrant(
  token: string,
  body: Record<string, unknown> = {},
) {
  const res = await fetchWithTimeout(`${apiBase()}/public/owner-auth/grant`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, ...body }),
  });
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  return unwrapApi(raw) as {
    ok?: boolean;
    project?: ApiProject;
    ha_restart_dispatched?: boolean;
    error?: string;
  };
}

export async function cloudRebindProject(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/rebind`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  return data as {
    activation_id: string;
    activation_key: string;
    user_code: string;
    qr_payload?: string;
    expires_in: number;
    project_id: string;
    note?: string;
  };
}

export async function cloudRevokeGateway(gatewayId: string, reason?: string) {
  return authedFetch(`/gateways/${gatewayId}/revoke`, {
    method: "POST",
    body: JSON.stringify({ reason: reason || "integrator_revoke" }),
  });
}

export type HostUnbindRequest = {
  id: string;
  projectId: string;
  gatewayId: string;
  requestedBy: string;
  reason?: string | null;
  status: string;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  createdAt: string;
};

/** 解绑主机：管理员直接执行；非管理员提交申请 */
export async function cloudUnbindHost(
  projectId: string,
  body?: { reason?: string },
) {
  const data = await authedFetch(`/projects/${projectId}/unbind-host`, {
    method: "POST",
    body: JSON.stringify(body || {}),
  });
  return data as {
    status: "executed" | "pending";
    message?: string;
    request?: HostUnbindRequest | null;
  };
}

export async function cloudGetPendingUnbind(projectId: string) {
  const data = await authedFetch(
    `/projects/${projectId}/unbind-host/pending`,
  );
  return data as { request: HostUnbindRequest | null };
}

export async function cloudApproveUnbind(requestId: string) {
  return authedFetch(`/projects/unbind-host/${requestId}/approve`, {
    method: "POST",
    body: JSON.stringify({}),
  }) as Promise<{ status: string; message?: string }>;
}

export async function cloudRejectUnbind(
  requestId: string,
  reason?: string,
) {
  return authedFetch(`/projects/unbind-host/${requestId}/reject`, {
    method: "POST",
    body: JSON.stringify({ reason: reason || undefined }),
  }) as Promise<{ status: string; message?: string }>;
}

/** 租户管理员 / 平台管理员可直接解绑或审批 */
export function canApproveHostUnbind(role: string | null | undefined): boolean {
  return (
    role === "platform_admin" ||
    role === "integrator_admin" ||
    role === "管理员"
  );
}

export async function cloudCommand(
  projectId: string,
  cmd: {
    kind?: string;
    domain?: string;
    service?: string;
    target?: Record<string, string>;
    data?: Record<string, unknown>;
    entities?: string[] | Record<string, unknown>;
    action?: string;
    name?: string;
    scene_id?: string;
    path?: string;
    content?: string;
    reload_services?: string[];
    requires_elevated?: boolean;
    slug?: string;
    entry_id?: string;
    user_id?: string;
    password?: string;
    command?: string;
    version?: string;
    write_scope?: string;
    [key: string]: unknown;
  },
) {
  return authedFetch(`/projects/${projectId}/command`, {
    method: "POST",
    body: JSON.stringify(cmd),
  });
}

export type CloudCommandReceipt = {
  command_id: string;
  gateway_id?: string;
  status: "pending" | "ok" | "failed" | "denied" | "timeout" | "unknown";
  kind?: string;
  error?: string;
  service?: string;
  result?: Record<string, unknown>;
  dispatched_at?: string;
  completed_at?: string;
};

export type AgentCapabilities = {
  project_id: string;
  gateway_id: string | null;
  agent_online: boolean;
  delivery_status: string;
  write_allowed: boolean;
  capability_tier: string;
  allowed_domains: string[];
  extended_domains: string[];
  supervisor_ops: boolean;
  config_write_scope: string;
  allowed_command_kinds: string[];
  maintenance_command_kinds: string[];
  catalog_version?: number | null;
  capability_manifest?: {
    catalog_version?: number;
    kinds?: Record<string, string[]>;
    aliases?: Record<string, { kind: string; action: string }>;
    ha_ws_allowlist?: string[];
    denylist_notes?: string[];
  } | null;
  /** Agent-probed HA features (doc 20 §7) */
  ha_features?: {
    ha_version?: string;
    registry_schema?: string;
    child_devices?: boolean;
    device_registry_remove?: boolean;
    list_linked_devices?: boolean;
    list_composite_splits?: boolean;
  } | null;
};

export async function cloudListAgentCapabilities(projectId: string) {
  const data = await authedFetch(
    `/projects/${projectId}/agent-capabilities`,
  );
  return data as AgentCapabilities;
}

export type CapabilityReportItem = {
  key: string;
  label: string;
  since: string;
  note: string;
  deadline?: string;
  expected: boolean;
  probed: boolean | null;
  gap: boolean;
  gainIfUpgrade: boolean;
  upgradeHint: string | null;
};

export type CapabilityReport = {
  projectId: string;
  gatewayId?: string | null;
  agentOnline?: boolean;
  haVersion: string | null;
  catalogVersion: string;
  baseline: string;
  items: CapabilityReportItem[];
  gaps: CapabilityReportItem[];
  gainsIfUpgrade: CapabilityReportItem[];
  gates: {
    deviceRegistryRemove: {
      allowed: boolean;
      reason: string | null;
    };
  };
  hassioAvailable?: boolean | null;
  pipDepsOk?: boolean | null;
  pipDeps?: Record<string, unknown> | null;
};

export async function cloudGetCapabilityReport(projectId: string) {
  const data = await authedFetch(
    `/projects/${projectId}/capability-report`,
  );
  return data as CapabilityReport;
}

export async function cloudListHaCapabilityCatalog() {
  const data = await authedFetch(`/meta/ha-capabilities`);
  return data as {
    catalogVersion: string;
    baseline: string;
    items: Array<{
      key: string;
      label: string;
      since: string;
      note: string;
      deadline?: string | null;
      probeField: string;
    }>;
  };
}

export type CoreUpgradePrecheck = {
  projectId: string;
  allowed: boolean;
  blockers: string[];
  warnings: string[];
  installType: string;
  supervisorRequired: boolean;
  currentVersion: string | null;
  targetVersion: string | null;
  gains: CapabilityReportItem[];
  alreadyHave: string[];
  requireBackup: boolean;
  requirePrecheckAck: boolean;
  agentOnline?: boolean;
};

export async function cloudGetCoreUpgradePrecheck(
  projectId: string,
  target?: string,
) {
  const q = target ? `?target=${encodeURIComponent(target)}` : "";
  const data = await authedFetch(
    `/projects/${projectId}/core-upgrade/precheck${q}`,
  );
  return data as CoreUpgradePrecheck;
}

export async function cloudGetCoreUpgradePostcheck(projectId: string) {
  const data = await authedFetch(
    `/projects/${projectId}/core-upgrade/postcheck`,
  );
  return data as {
    projectId: string;
    agentOnline: boolean;
    haVersion: string | null;
    rulePackVersion: string;
    checks: string[];
    ok: boolean;
    notes: string[];
    agentResult?: Record<string, unknown> | null;
    generatedAt: string;
  };
}

export async function cloudGetRootCausePreview(
  projectId: string,
  opts: {
    entityId: string;
    at?: string;
    windowMs?: number;
    limit?: number;
    alertId?: string;
    ticketId?: string;
    persist?: boolean;
  },
) {
  const q = new URLSearchParams();
  q.set("entity_id", opts.entityId);
  if (opts.at) q.set("at", opts.at);
  if (opts.windowMs != null) q.set("window_ms", String(opts.windowMs));
  if (opts.limit != null) q.set("limit", String(opts.limit));
  if (opts.alertId) q.set("alert_id", opts.alertId);
  if (opts.ticketId) q.set("ticket_id", opts.ticketId);
  if (opts.persist) q.set("persist", "1");
  const data = await authedFetch(
    `/projects/${projectId}/root-cause?${q.toString()}`,
  );
  return data as RootCauseAnalysisResult;
}

export type RootCauseAnalysisResult = {
  projectId: string;
  entityId: string;
  at: string;
  windowMs: number;
  agentOnline: boolean;
  automationsKnown: number;
  hits: Array<{
    entityId: string;
    name: string;
    source: "logbook" | "trace";
    deltaMs: number | null;
    message?: string;
    runId?: string;
    trigger?: string;
  }>;
  notes: string[];
  generatedAt: string;
  alertId?: string | null;
  ticketId?: string | null;
  analysisId?: string | null;
};

/** Analyze + persist (default). Prefer when linking from alert/ticket. */
export async function cloudAnalyzeRootCause(
  projectId: string,
  opts: {
    entityId?: string;
    at?: string;
    windowMs?: number;
    limit?: number;
    alertId?: string;
    ticketId?: string;
    persist?: boolean;
  },
) {
  const data = await authedFetch(`/projects/${projectId}/root-cause`, {
    method: "POST",
    body: JSON.stringify({
      entity_id: opts.entityId,
      at: opts.at,
      window_ms: opts.windowMs,
      limit: opts.limit,
      alert_id: opts.alertId,
      ticket_id: opts.ticketId,
      persist: opts.persist !== false,
    }),
  });
  return data as RootCauseAnalysisResult;
}

export async function cloudListRootCauseAnalyses(
  projectId: string,
  opts: { alertId?: string; ticketId?: string; limit?: number } = {},
) {
  const q = new URLSearchParams();
  if (opts.alertId) q.set("alert_id", opts.alertId);
  if (opts.ticketId) q.set("ticket_id", opts.ticketId);
  if (opts.limit != null) q.set("limit", String(opts.limit));
  const qs = q.toString();
  const data = await authedFetch(
    `/projects/${projectId}/root-cause/analyses${qs ? `?${qs}` : ""}`,
  );
  return data as {
    items: Array<{
      id: string;
      projectId: string;
      alertId: string | null;
      ticketId: string | null;
      entityId: string;
      at: string;
      windowMs: number;
      agentOnline: boolean;
      hitCount: number;
      hits: unknown;
      notes: unknown;
      actor: string | null;
      createdAt: string;
    }>;
  };
}

export type OpsPlaybook = {
  projectId: string;
  installForm: string;
  supervisorOps: boolean;
  sections: Record<string, { enabled: boolean; reason: string | null }>;
  diagnostics: {
    brandTree: boolean;
    protocolTree: boolean;
    matterRpc: boolean;
  };
  notes: string[];
  rulePackVersion?: string;
  matchedRuleIds?: string[];
  postUpgradeChecks?: string[];
};

export async function cloudGetOpsPlaybook(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/ops-playbook`);
  return data as OpsPlaybook;
}

export type LinkDiagnosisCloud = {
  projectId: string;
  agentOnline: boolean;
  lastPing: string | null;
  pingAgeSec: number | null;
  installType: string | null;
  haVersion: string | null;
  devices: {
    total: number;
    online: number;
    offline: number;
    offlineSample: Array<{ id: string; name: string; manufacturer: string | null }>;
  };
  domains: string[];
  brandTree: {
    enabled: boolean;
    nodes: Array<{ id: string; label: string; status: string; hint: string }>;
  };
  protocolTree: {
    enabled: boolean;
    nodes: Array<{ id: string; label: string; status: string; hint: string }>;
  };
  agentProbe?: Record<string, unknown> | null;
  rating: "低风险" | "中风险" | "高风险";
  anomalyRate: number;
  notes: string[];
  generatedAt: string;
};

export async function cloudGetLinkDiagnosis(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/link-diagnosis`);
  return data as LinkDiagnosisCloud;
}

export type AutomationSchemaHint = {
  projectId?: string;
  haVersion: string | null;
  availableFeatures: string[];
  hints: Array<{ haMin: string; features: string[]; note: string }>;
  blueprintHint: string;
};

export async function cloudGetAutomationSchema(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/automation-schema`);
  return data as AutomationSchemaHint;
}

export type DeviceRefItem = {
  id: string;
  logicalId: string;
  haDeviceId: string;
  previousHaDeviceId?: string | null;
  fingerprint: string;
  name?: string | null;
  status: string;
  updatedAt?: string;
};

export async function cloudListDeviceRefs(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/device-refs`);
  return data as { items: DeviceRefItem[]; total: number };
}

export async function cloudReconcileDeviceRefs(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/device-refs/reconcile`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  return data as {
    command_id?: string;
    status: string;
    remapped?: number;
    splits?: number;
    error?: string;
  };
}

export async function cloudGetCommandResult(
  projectId: string,
  commandId: string,
) {
  const data = await authedFetch(
    `/projects/${projectId}/commands/${encodeURIComponent(commandId)}`,
  );
  return data as CloudCommandReceipt;
}

/** Poll Agent command.result until settled or timeout (default 8s). */
export async function cloudWaitCommandResult(
  projectId: string,
  commandId: string,
  opts?: { timeoutMs?: number; intervalMs?: number },
): Promise<CloudCommandReceipt> {
  const timeoutMs = opts?.timeoutMs ?? 8000;
  const intervalMs = opts?.intervalMs ?? 300;
  const deadline = Date.now() + timeoutMs;
  let last: CloudCommandReceipt = {
    command_id: commandId,
    status: "pending",
  };
  while (Date.now() < deadline) {
    try {
      last = await cloudGetCommandResult(projectId, commandId);
      if (last.status !== "pending" && last.status !== "unknown") {
        return last;
      }
    } catch {
      /* keep polling */
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return last.status === "pending"
    ? { ...last, status: "timeout", error: last.error || "wait_timeout" }
    : last;
}

export type CommandChannelProbeResult = {
  ws_online: boolean;
  command_ok: boolean;
  status: string;
  kind: string | null;
  rtt_ms: number | null;
  command_id: string | null;
  error: string | null;
  last_ping: string | null;
  agent_version: string | null;
  /** HA 2026.8/9 Registry 字段抽查（config_entry_id / parent_device_id） */
  registry_check?: {
    schema: string | null;
    child_devices: boolean;
    device_total: number;
    missing_config_entry_id: number;
    child_device_count: number;
    ok: boolean;
  } | null;
  message: string;
};

/** Heartbeat ≠ command path — server waits for a real command.result. */
export async function cloudProbeCommandChannel(projectId: string) {
  const data = await authedFetch(
    `/projects/${projectId}/command-channel-probe`,
    { method: "POST", body: JSON.stringify({}) },
  );
  return data as CommandChannelProbeResult;
}

/** Ask connected Agent to reload scene/automation and push sync.full to cloud. */
export async function cloudRequestHostSync(projectId: string) {
  return cloudCommand(projectId, { kind: "sync_full" }) as Promise<{
    command_id: string;
    status: string;
  }>;
}

export type ProjectSyncResult = {
  push: {
    attempted: boolean;
    skipped: string | null;
    dispatched: number;
    items: Array<{
      revision_id: string;
      filename: string;
      kind: string;
      status: string;
      command_id?: string;
      error?: string;
    }>;
  };
  pull: { command_id: string; status: string; skipped?: string } | null;
  message: string;
};

/**
 * Bidirectional sync: cloud config authority (push pending/failed) then pull HA mirror.
 */
export async function cloudSyncWithHost(
  projectId: string,
  opts: { push?: boolean; pull?: boolean } = {},
) {
  return (await authedFetch(`/projects/${projectId}/sync`, {
    method: "POST",
    body: JSON.stringify({
      push: opts.push !== false,
      pull: opts.pull !== false,
    }),
  })) as ProjectSyncResult;
}

/** Scene reconcile: unique codes; cloud wins on conflict; ingest HA-native. */
export async function cloudReconcileScenes(projectId: string) {
  return (await authedFetch(`/projects/${projectId}/scenes/reconcile`, {
    method: "POST",
    body: JSON.stringify({}),
  })) as {
    status: string;
    unique_codes: number;
    summary: Record<string, number>;
    items: Array<{
      action: string;
      code?: string;
      entity_id?: string;
      name?: string;
      detail?: string;
    }>;
    message: string;
  };
}

/** Cloud OTA: download release tarball on Agent and reload integration. */
export async function cloudUpdateAgent(projectId: string) {
  return authedFetch(`/projects/${projectId}/agent-update`, {
    method: "POST",
    body: JSON.stringify({}),
  }) as Promise<{
    command_id?: string;
    status: string;
    version?: string;
    previous_version?: string;
    skipped?: boolean;
    message?: string;
    ssh_bypass_ensure?: {
      skipped?: boolean;
      reason?: string;
      dispatched?: boolean;
      command_id?: string;
      status?: string;
    };
  }>;
}

/** Stage device firmware into SaaS mirror, then dispatch HA update.install. */
export async function cloudApplyFirmwareUpdate(
  projectId: string,
  entityId: string,
) {
  return authedFetch(`/projects/${projectId}/firmware-updates/apply`, {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId }),
  }) as Promise<{
    mirror: {
      id: string;
      entity_id: string;
      title: string;
      installed_version: string | null;
      latest_version: string | null;
      status: string;
      has_package: boolean;
      note?: string;
    };
    command_id?: string;
    status: string;
    message?: string;
    pipeline?: Array<{ id: string; label: string; status: string }>;
  }>;
}

export async function cloudListFirmwareMirrors(projectId: string) {
  return authedFetch(`/projects/${projectId}/firmware-mirrors`) as Promise<{
    items: Array<{
      id: string;
      entity_id: string;
      title: string;
      installed_version: string | null;
      latest_version: string | null;
      status: string;
      has_package: boolean;
      mirrored_at: string;
      dispatched_at?: string;
      note?: string;
    }>;
  }>;
}

export async function cloudListIntegrationMirrors() {
  const res = await fetch(`${apiBase()}/agent/integration-mirrors`);
  const body = await res.json();
  const data =
    body && body.data !== undefined && body.message !== undefined
      ? body.data
      : body;
  if (!res.ok) throw new Error(data?.message || "mirror_catalog_failed");
  return data as {
    catalog?: { updated_at?: string; packages?: unknown[] };
    packages: Array<{
      domain: string;
      label?: string;
      upstream_repo?: string;
      latest?: string;
      version?: string;
      sha256?: string;
      size?: number;
      download_url?: string | null;
      package?: {
        domain?: string;
        version?: string;
        release_tag?: string;
        asset?: string;
      } | null;
    }>;
  };
}

export async function cloudGetAgentReleaseLatest() {
  const res = await fetch(`${apiBase()}/agent/releases/latest`);
  const body = await res.json();
  const data =
    body && body.data !== undefined && body.message !== undefined
      ? body.data
      : body;
  if (!res.ok) throw new Error(data?.message || "release_fetch_failed");
  return data as { release: { version: string; sha256: string; size: number } };
}

/** 测试用：设置项目 Agent 版本号 */
export async function cloudDebugSetAgentVersion(
  projectId: string,
  version: string,
) {
  return authedFetch(`/projects/${projectId}/debug-agent-version`, {
    method: "PATCH",
    body: JSON.stringify({ version }),
  }) as Promise<{ ok: boolean; projectId: string; agentVersion: string }>;
}

/** True when id is managed by cloud-min (not pure local mock). */
export function isCloudProjectId(id: string) {
  if (id === "prj_demo_xihu") return true;
  if (!/^prj_[a-z0-9]+$/i.test(id)) return false;
  // 离线建档：prj_1735123456789（纯数字后缀）
  if (/^prj_\d{10,}$/.test(id)) return false;
  return true;
}

/* —— Ops: alerts / rules / tickets —— */

export type ApiAlert = {
  id: string;
  projectId: string;
  projectName: string;
  level: "提示" | "一般" | "严重";
  rule: string;
  ruleId?: string | null;
  target: string;
  status: "活动" | "已恢复" | "已静默" | "已处理";
  createdAt: string;
  notified: string;
  notifyStatus?: string | null;
  ticketId?: string | null;
  signature?: string | null;
  source?: string;
};

export type ApiRule = {
  id: string;
  name: string;
  level: "提示" | "一般" | "严重";
  trigger: string;
  channels: string[];
  quietMinutes: number;
  enabled: boolean;
  autoTicket: boolean;
  source?: string;
};

export type ApiTicket = {
  id: string;
  projectId: string;
  projectName: string;
  source: "客户报障" | "告警" | "手动";
  title: string;
  priority: "低" | "中" | "高";
  status: "待分派" | "处理中" | "待回访" | "已关闭" | "已取消";
  assignee: string;
  createdAt: string;
  device?: string;
  needVisit?: boolean;
  alertId?: string;
  signature?: string | null;
  alertCount?: number;
  openAlertCount?: number;
  diagnosis?: string;
  serviceNote?: string;
  timeline?: { at: string; actor: string; text: string }[];
};

export async function cloudListAlerts() {
  const data = await authedFetch("/alerts");
  return data as { items: ApiAlert[] };
}

export async function cloudAlertAction(
  id: string,
  action: "silence" | "recover" | "to-ticket",
) {
  return authedFetch(`/alerts/${id}/${action}`, { method: "POST" }) as Promise<{
    alert?: ApiAlert;
    ticket?: ApiTicket;
  }>;
}

export async function cloudListRules() {
  const data = await authedFetch("/alert-rules");
  return data as { items: ApiRule[] };
}

export async function cloudUpsertRule(rule: ApiRule) {
  const existing = await cloudListRules().catch(() => ({ items: [] as ApiRule[] }));
  const found = existing.items.some((r) => r.id === rule.id);
  if (found) {
    const data = await authedFetch(`/alert-rules/${rule.id}`, {
      method: "PUT",
      body: JSON.stringify(rule),
    });
    return data as { rule: ApiRule };
  }
  const data = await authedFetch("/alert-rules", {
    method: "POST",
    body: JSON.stringify(rule),
  });
  return data as { rule: ApiRule };
}

export async function cloudDeleteRule(id: string) {
  return authedFetch(`/alert-rules/${id}`, { method: "DELETE" });
}

export async function cloudToggleRule(id: string) {
  const data = await authedFetch(`/alert-rules/${id}/toggle`, {
    method: "POST",
  });
  return data as { rule: ApiRule };
}

export async function cloudListTickets() {
  const data = await authedFetch("/tickets");
  return data as { items: ApiTicket[] };
}

export async function cloudAdvanceTicket(
  id: string,
  status?: string,
) {
  const data = await authedFetch(`/tickets/${id}/advance`, {
    method: "POST",
    body: JSON.stringify(status ? { status } : {}),
  });
  return data as { ticket: ApiTicket };
}

export async function cloudAssignTicket(
  id: string,
  assignee: string,
  note?: string,
) {
  const data = await authedFetch(`/tickets/${id}/assign`, {
    method: "POST",
    body: JSON.stringify({ assignee, note }),
  });
  return data as { ticket: ApiTicket };
}

export async function cloudAddTicketEvent(id: string, text: string) {
  const data = await authedFetch(`/tickets/${id}/events`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  return data as { ticket: ApiTicket };
}

export async function cloudUpdateTicket(
  id: string,
  patch: {
    diagnosis?: string;
    serviceNote?: string;
    title?: string;
    priority?: string;
  },
) {
  const data = await authedFetch(`/tickets/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return data as { ticket: ApiTicket };
}

export async function cloudCreateTicket(body: {
  projectId: string;
  title: string;
  priority?: string;
  assignee?: string;
  needVisit?: boolean;
}) {
  const data = await authedFetch("/tickets", {
    method: "POST",
    body: JSON.stringify({
      project_id: body.projectId,
      title: body.title,
      priority: body.priority,
      assignee: body.assignee,
      need_visit: body.needVisit,
    }),
  });
  return data as { ticket: ApiTicket };
}

/** Export tickets as JSON or CSV (MVP DoD #5). */
export async function cloudExportTickets(opts?: {
  projectId?: string;
  format?: "json" | "csv";
}) {
  const params = new URLSearchParams();
  if (opts?.format) params.set("format", opts.format);
  const path = opts?.projectId
    ? `/projects/${opts.projectId}/tickets/export?${params}`
    : `/tickets/export?${params}`;
  const token = getCloudToken();
  if (!token) throw Object.assign(new Error("no_token"), { code: "no_token" });
  const res = await fetch(`${apiBase()}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw Object.assign(new Error("export_failed"), err);
  }
  if (opts?.format === "csv") {
    return { csv: await res.text() };
  }
  return (await res.json()) as { items: ApiTicket[]; total: number };
}

/* —— Elevated sessions (PRD §5.4F) —— */

export type ApiElevatedNotify = {
  sms: { attempted: boolean; delivered: boolean; channel?: string };
  email: { attempted: boolean; delivered: boolean };
  ownerLink: string;
  phoneMasked?: string | null;
  emailMasked?: string | null;
  preview?: string;
};

export type ApiElevatedSession = {
  id: string;
  projectId: string;
  projectName: string;
  requester: string;
  reason: string;
  scope: "维护" | "配置" | string;
  durationMinutes: number;
  durationLabel: string;
  status: "pending_approval" | "active" | "expired" | "revoked" | "rejected" | string;
  statusLabel: string;
  approvalChannel?: string | null;
  approver?: string | null;
  requestedAt: string;
  approvedAt?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  revokedAt?: string | null;
  revokeReason?: string | null;
  elevatedToken?: string;
  ownerLink?: string;
  notify?: ApiElevatedNotify;
  allowConsoleSim?: boolean;
};

export async function cloudListElevatedSessions(opts?: {
  activeOnly?: boolean;
  status?: string;
}) {
  const q = new URLSearchParams();
  if (opts?.activeOnly) q.set("active_only", "1");
  if (opts?.status) q.set("status", opts.status);
  const suffix = q.toString() ? `?${q}` : "";
  const data = await authedFetch(`/elevated-sessions${suffix}`);
  return data as {
    items: ApiElevatedSession[];
    duration_tiers: number[];
  };
}

export async function cloudListProjectElevatedSessions(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/elevated-sessions`);
  return data as {
    items: ApiElevatedSession[];
    active: ApiElevatedSession | null;
    allowConsoleSim?: boolean;
    duration_tiers: number[];
  };
}

export async function cloudRequestElevatedSession(
  projectId: string,
  body: {
    reason: string;
    scope: "维护" | "配置";
    durationMinutes: 30 | 120 | 480;
    requester?: string;
  },
) {
  const data = await authedFetch(`/projects/${projectId}/elevated-sessions`, {
    method: "POST",
    body: JSON.stringify({
      reason: body.reason,
      scope: body.scope,
      duration_minutes: body.durationMinutes,
      requester: body.requester,
    }),
  });
  return data as {
    session: ApiElevatedSession;
    autoApproved?: boolean;
    ownerLink?: string;
    notify?: ApiElevatedNotify;
    approvalToken?: string;
  };
}

export async function cloudResendElevatedNotify(sessionId: string) {
  const data = await authedFetch(`/elevated-sessions/${sessionId}/notify`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  return data as {
    session: ApiElevatedSession;
    ownerLink?: string;
    notify?: ApiElevatedNotify;
    approvalToken?: string;
  };
}

export async function cloudApproveElevatedSession(
  sessionId: string,
  body?: { channel?: string; approver?: string },
) {
  const data = await authedFetch(`/elevated-sessions/${sessionId}/approve`, {
    method: "POST",
    body: JSON.stringify({
      approval_channel: body?.channel || "console_sim",
      channel: body?.channel || "console_sim",
      approver: body?.approver || "业主（控制台模拟）",
    }),
  });
  return data as { session: ApiElevatedSession };
}

export async function cloudRevokeElevatedSession(
  sessionId: string,
  body?: { reason?: string; actor?: string },
) {
  const data = await authedFetch(`/elevated-sessions/${sessionId}/revoke`, {
    method: "POST",
    body: JSON.stringify(body || {}),
  });
  return data as { session: ApiElevatedSession };
}

export async function publicElevatedPreview(token: string) {
  const res = await fetchWithTimeout(
    `${apiBase()}/public/elevated?token=${encodeURIComponent(token)}`,
  );
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  return unwrapApi(raw) as {
    session?: {
      id: string;
      projectName: string;
      requester: string;
      reason: string;
      scope: string;
      durationLabel: string;
      status: string;
      statusLabel: string;
      requestedAt: string;
    };
    error?: string;
  };
}

export async function publicElevatedDecide(
  token: string,
  decision: "approve" | "reject",
) {
  const res = await fetchWithTimeout(`${apiBase()}/public/elevated/decide`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, decision }),
  });
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  return unwrapApi(raw) as {
    ok?: boolean;
    decision?: string;
    session?: ApiElevatedSession;
    error?: string;
  };
}

export async function cloudConfigWriteCheck(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/config-write-check`);
  return data as {
    ok: boolean;
    error: string | null;
    active_session: ApiElevatedSession | null;
  };
}

export type ApiConfigRevision = {
  id: string;
  projectId: string;
  kind: string;
  name: string;
  filename: string;
  version: number;
  content: string;
  reloadServices: string[];
  status: string;
  source: string;
  operator?: string | null;
  elevatedSessionId?: string | null;
  commandId?: string | null;
  error?: string | null;
  createdAt: string;
  appliedAt?: string | null;
};

export async function cloudConfigWrite(
  projectId: string,
  body: {
    kind: "scene" | "automation" | "script" | "raw";
    name: string;
    entities?: string;
    description?: string;
    yaml?: string;
    filename?: string;
    preview?: boolean;
    source?: string;
    automation?: {
      triggerEntityId?: string;
      triggerTo?: string;
      actionEntityId?: string;
      actionService?: string;
    };
  },
) {
  const data = await authedFetch(`/projects/${projectId}/config-write`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data as {
    status?: string;
    command_id?: string;
    revision?: ApiConfigRevision;
    preview?: {
      filename: string;
      version: number;
      reloadServices: string[];
      content?: string;
      bytes?: number;
    };
  };
}

export async function cloudListConfigRevisions(
  projectId: string,
  opts?: { kind?: string; limit?: number },
) {
  const q = new URLSearchParams();
  if (opts?.kind) q.set("kind", opts.kind);
  if (opts?.limit) q.set("limit", String(opts.limit));
  const qs = q.toString();
  const data = await authedFetch(
    `/projects/${projectId}/config-revisions${qs ? `?${qs}` : ""}`,
  );
  return data as { items: ApiConfigRevision[] };
}

export async function cloudRollbackConfigRevision(
  projectId: string,
  revisionId: string,
) {
  const data = await authedFetch(
    `/projects/${projectId}/config-revisions/${revisionId}/rollback`,
    { method: "POST", body: "{}" },
  );
  return data as {
    status: string;
    command_id: string;
    revision: ApiConfigRevision;
  };
}

export type ApiConfigDrift = {
  id: string;
  kind: string;
  path?: string | null;
  entityId?: string | null;
  expectedHash?: string | null;
  observedHash?: string | null;
  detail?: Record<string, unknown> | null;
  status: string;
  snapshotId?: string | null;
  createdAt: string;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolutionNote?: string | null;
};

export async function cloudCollectConfigSnapshot(projectId: string) {
  const data = await authedFetch(`/projects/${projectId}/config-snapshot`, {
    method: "POST",
    body: "{}",
  });
  return data as {
    status: string;
    error?: string;
    command_id?: string;
    snapshot_id?: string;
    open_drifts?: number;
    drifts?: ApiConfigDrift[];
  };
}

export async function cloudListConfigDrifts(
  projectId: string,
  status = "open",
) {
  const q = new URLSearchParams({ status });
  const data = await authedFetch(
    `/projects/${projectId}/config-drifts?${q.toString()}`,
  );
  return data as {
    items: ApiConfigDrift[];
    latestSnapshot: {
      id: string;
      manifestSha256?: string | null;
      createdAt: string;
      source: string;
      payload?: Record<string, unknown>;
    } | null;
  };
}

export async function cloudResolveConfigDrift(
  projectId: string,
  driftId: string,
  body: { action: "overwrite" | "adopt" | "dismiss"; note?: string },
) {
  const data = await authedFetch(
    `/projects/${projectId}/config-drifts/${driftId}/resolve`,
    { method: "POST", body: JSON.stringify(body) },
  );
  return data as {
    status: string;
    error?: string;
    hint?: string;
    drift_id?: string;
    resolution?: string;
    command_id?: string | null;
    command_ids?: string[];
    revision_id?: string;
    filename?: string;
    next?: Record<string, unknown> | null;
  };
}

export async function cloudRecordHaWriteback(
  projectId: string,
  body: {
    entityId?: string;
    domain?: string;
    path?: string;
    note?: string;
  },
) {
  const data = await authedFetch(
    `/projects/${projectId}/config-drifts/ha-writeback`,
    { method: "POST", body: JSON.stringify(body) },
  );
  return data as { status: string; drift_id?: string; error?: string };
}

export async function cloudConfigRedeploy(
  projectId: string,
  kind: "scene" | "automation" | "script",
) {
  const data = await authedFetch(`/projects/${projectId}/config-redeploy`, {
    method: "POST",
    body: JSON.stringify({ kind }),
  });
  return data as {
    status: string;
    count: number;
    items: { command_id: string; revision: ApiConfigRevision }[];
  };
}

export async function cloudConfigDelete(
  projectId: string,
  body: {
    kind?: "scene" | "automation" | "script";
    channel?: "managed" | "runtime" | "ha";
    name?: string;
    entityId?: string;
    filename?: string;
  },
) {
  const data = await authedFetch(`/projects/${projectId}/config-delete`, {
    method: "POST",
    body: JSON.stringify({
      kind: body.kind || "scene",
      channel: body.channel || "managed",
      name: body.name,
      entity_id: body.entityId,
      filename: body.filename,
    }),
  });
  return data as {
    status: string;
    channel?: string;
    command_id?: string;
    entity_id?: string;
    already_deleted?: boolean;
    revision?: ApiConfigRevision | null;
  };
}

/* —— Pairing codes (PRD §5.3A) —— */

export type ApiPairingCode = {
  id: string;
  projectId: string;
  code: string;
  codeType: string;
  protocol: string;
  label: string;
  room: string;
  deviceName: string;
  deviceId?: string | null;
  entityId?: string | null;
  status: "unused" | "paired" | "invalid" | string;
  statusLabel: string;
  note: string;
  source: string;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
  pairedAt?: string | null;
};

export async function cloudListPairingCodes(
  projectId: string,
  opts?: { protocol?: string; status?: string; room?: string; q?: string },
) {
  const q = new URLSearchParams();
  if (opts?.protocol) q.set("protocol", opts.protocol);
  if (opts?.status) q.set("status", opts.status);
  if (opts?.room) q.set("room", opts.room);
  if (opts?.q) q.set("q", opts.q);
  const suffix = q.toString() ? `?${q}` : "";
  const data = await authedFetch(
    `/projects/${projectId}/pairing-codes${suffix}`,
  );
  return data as { items: ApiPairingCode[]; protocols: string[] };
}

export async function cloudCreatePairingCode(
  projectId: string,
  body: {
    code: string;
    protocol?: string;
    label?: string;
    room?: string;
    deviceName?: string;
    note?: string;
    source?: string;
  },
) {
  const data = await authedFetch(`/projects/${projectId}/pairing-codes`, {
    method: "POST",
    body: JSON.stringify({
      code: body.code,
      protocol: body.protocol,
      label: body.label,
      room: body.room,
      device_name: body.deviceName,
      note: body.note,
      source: body.source || "manual",
    }),
  });
  return data as { code: ApiPairingCode };
}

export async function cloudUpdatePairingCode(
  projectId: string,
  codeId: string,
  body: Record<string, unknown>,
) {
  const data = await authedFetch(
    `/projects/${projectId}/pairing-codes/${codeId}`,
    {
      method: "PUT",
      body: JSON.stringify(body),
    },
  );
  return data as { code: ApiPairingCode };
}

export async function cloudDeletePairingCode(projectId: string, codeId: string) {
  return authedFetch(`/projects/${projectId}/pairing-codes/${codeId}`, {
    method: "DELETE",
  });
}

export async function cloudExtractPairingText(projectId: string, text: string) {
  const data = await authedFetch(
    `/projects/${projectId}/pairing-codes/extract`,
    {
      method: "POST",
      body: JSON.stringify({ text }),
    },
  );
  return data as {
    candidates: { code: string; code_type: string; protocol: string }[];
  };
}

/* —— Project files (PRD §5.2) —— */

export type ApiProjectFile = {
  id: string;
  projectId: string;
  stage: "design" | "construction" | "delivery" | "ops" | string;
  stageLabel: string;
  title: string;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  note: string;
  uploadedBy?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ApiFileStage = { id: string; label: string };

export async function cloudListProjectFiles(
  projectId: string,
  opts?: { stage?: string; q?: string },
) {
  const q = new URLSearchParams();
  if (opts?.stage) q.set("stage", opts.stage);
  if (opts?.q) q.set("q", opts.q);
  const suffix = q.toString() ? `?${q}` : "";
  const data = await authedFetch(`/projects/${projectId}/files${suffix}`);
  return data as {
    items: ApiProjectFile[];
    stages: ApiFileStage[];
    max_bytes: number;
  };
}

export async function cloudUploadProjectFile(
  projectId: string,
  input: {
    file: File;
    stage: string;
    title?: string;
    note?: string;
  },
) {
  const token = getCloudToken();
  if (!token) throw Object.assign(new Error("no_token"), { code: "no_token" });
  const fd = new FormData();
  fd.append("file", input.file);
  fd.append("stage", input.stage);
  if (input.title) fd.append("title", input.title);
  if (input.note) fd.append("note", input.note);
  const res = await fetch(`${apiBase()}/projects/${projectId}/files`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw Object.assign(new Error(data.error || "upload_failed"), {
      code: data.error,
      status: res.status,
      data,
    });
  }
  return data as { file: ApiProjectFile };
}

export async function cloudDeleteProjectFile(projectId: string, fileId: string) {
  return authedFetch(`/projects/${projectId}/files/${fileId}`, {
    method: "DELETE",
  });
}

export function cloudProjectFileDownloadUrl(projectId: string, fileId: string) {
  return `${apiBase()}/projects/${projectId}/files/${fileId}?download=1`;
}

export async function cloudDownloadProjectFile(
  projectId: string,
  fileId: string,
  filename: string,
) {
  const token = getCloudToken();
  if (!token) throw Object.assign(new Error("no_token"), { code: "no_token" });
  const res = await fetch(cloudProjectFileDownloadUrl(projectId, fileId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw Object.assign(new Error(data.error || "download_failed"), {
      code: data.error,
      status: res.status,
    });
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** AI Center — NL draft generation (PRD §5.4B / P2→P1-05) */
export type ApiAiDraft = {
  id: string;
  kind: "scene" | "automation";
  name: string;
  filename?: string;
  yaml?: string;
  content?: string;
  yaml_generated?: string;
  status?: string;
  version?: number;
  operator?: string | null;
  createdAt?: string;
  appliedAt?: string | null;
  entities_used?: string[];
  warnings?: string[];
  risk_labels?: string[];
  template_id?: string | null;
  engine?: "llm" | "rules" | string;
  prompt?: string;
};

export type ApiAiSceneTemplate = {
  id: "lighting" | "shade" | "av" | "climate";
  label: string;
  domains: string[];
  blurb: string;
};

export type ApiAiSceneReadiness = {
  enabled: boolean;
  reason: string | null;
  engine: "llm" | "rules";
  llm_configured: boolean;
  entitlement: boolean;
  global_enabled: boolean;
  tenant_enabled: boolean;
  project_enabled: boolean;
  can_toggle_project: boolean;
  can_toggle_tenant: boolean;
  can_toggle_global: boolean;
  quota: { used: number; limit: number };
  templates: ApiAiSceneTemplate[];
  areas?: Array<{ area_id: string; name: string }>;
  notice: string;
};

export async function cloudAiSceneReadiness(projectId: string) {
  return (await authedFetch(
    `/ai-center/scene/readiness?project_id=${encodeURIComponent(projectId)}`,
  )) as ApiAiSceneReadiness;
}

export async function cloudAiSceneSwitchState() {
  return (await authedFetch("/ai-center/scene/switch")) as {
    global_enabled: boolean;
  };
}

export async function cloudAiSceneSwitch(body: {
  scope: "global" | "tenant" | "project";
  enabled: boolean;
  project_id?: string;
}) {
  return (await authedFetch("/ai-center/scene/switch", {
    method: "PUT",
    body: JSON.stringify(body),
  })) as { scope: string; enabled: boolean };
}

export async function cloudAiGenerate(body: {
  project_id: string;
  description: string;
  kind?: "scene" | "automation";
  template_id?: string;
  area_id?: string;
  area_ids?: string[];
}) {
  const data = await authedFetch("/ai-center/generate", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data as ApiAiDraft;
}

export type ApiExecutableAction = {
  entity_id: string;
  domain: string;
  service: string;
  data?: Record<string, unknown>;
  label?: string;
};

export type ApiExecutableSubset = {
  id: string;
  skill_id: string;
  skill_label: string;
  project_id: string;
  reason: string;
  actions: ApiExecutableAction[];
  warnings: string[];
  requires_confirm: boolean;
  evaluated_at: string;
};

export async function cloudAiEvaluate(body: {
  project_id: string;
  skill_ids?: string[];
}) {
  const data = await authedFetch("/ai-center/evaluate", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data as { subsets: ApiExecutableSubset[]; entity_count: number };
}

export type ApiPerceptionSlot = {
  key: string;
  label: string;
  required: boolean;
  ready: boolean;
  entity_ids: string[];
  hint?: string;
};

export type ApiClimateDevice = {
  entity_id: string;
  name: string;
  room?: string | null;
  state?: string | null;
  available: boolean;
  channel: "A" | "B" | "C";
  channel_source: string;
  capability_label: string;
  manufacturer?: string | null;
  model?: string | null;
  platform_hint?: string | null;
};

export type ApiClimateSleepParams = {
  template_id: "sleep_curve";
  alias: string;
  climate_entity_id: string;
  temp_entity_id: string;
  occupancy_entity_id: string;
  start_at: string;
  intervene_above: number;
  setpoint: number;
  hvac_mode: string;
  fan_mode?: string;
  temp_floor: number;
  temp_ceiling: number;
};

export type ApiClimateDraft = {
  template_id: string;
  project_id: string;
  params: ApiClimateSleepParams;
  yaml: string;
  filename: string;
  channel: "A" | "B" | "C";
  warnings: string[];
  entities_used: string[];
  evaluated_at: string;
  revision_id?: string;
  write_hint?: {
    kind: "automation";
    name: string;
    yaml: string;
    filename: string;
    source: string;
  };
};

export type ApiClimateBaseline = {
  enabled: boolean;
  started_at: string | null;
  sample_count: number;
  climate_entity_ids: string[];
  last_sample_at: string | null;
};

export async function cloudAiClimateReadiness(projectId: string) {
  return (await authedFetch(
    `/ai-center/climate/${projectId}/readiness`,
  )) as {
    perception: {
      ready: boolean;
      min_ready: boolean;
      slots: ApiPerceptionSlot[];
      warnings: string[];
    };
    climates: ApiClimateDevice[];
    templates: Array<{
      id: string;
      label: string;
      description: string;
      implemented: boolean;
      min_channel: string;
    }>;
    algorithms?: ApiAiAlgorithmGate[];
    entity_count: number;
  };
}

export type ApiAiMissingItem = {
  key: string;
  label: string;
  severity: "blocking" | "recommended";
  suggest: string;
  ready?: false;
  hint?: string;
};

export type ApiAiAlgorithmGate = {
  algorithm_id: string;
  label: string;
  description: string;
  enabled: boolean;
  can_run: boolean;
  implemented: boolean;
  missing: ApiAiMissingItem[];
  found: Array<{ key: string; label: string; entity_ids: string[] }>;
  missing_summary: string;
  prompt?: {
    title: string;
    body: string;
    items: Array<{ label: string; severity: string; suggest: string }>;
  } | null;
};

export async function cloudAiListAlgorithms(projectId: string) {
  return (await authedFetch(`/ai-center/algorithms/${projectId}`)) as {
    algorithms: ApiAiAlgorithmGate[];
    boards: ApiAiAlgorithmBoard[];
    entity_count: number;
  };
}

export type ApiAiAlgorithmRule = {
  id: string;
  name: string;
  summary: string;
  mode: "packages" | "instant";
  status: string;
  template_id?: string;
  filename?: string;
  yaml?: string;
  revision_id?: string;
  version?: number;
};

export type ApiAiAlgorithmBoard = {
  id: string;
  label: string;
  description: string;
  family: "climate" | "subset";
  algorithm_id: string;
  enabled: boolean;
  can_run: boolean;
  implemented: boolean;
  missing: ApiAiMissingItem[];
  found: Array<{ key: string; label: string; entity_ids: string[] }>;
  missing_summary: string;
  rules: ApiAiAlgorithmRule[];
  addable_templates: Array<{
    id: string;
    label: string;
    implemented: boolean;
  }>;
};

export async function cloudAiSetAlgorithm(
  projectId: string,
  body: { algorithm_id: string; enabled: boolean },
) {
  return (await authedFetch(`/ai-center/algorithms/${projectId}`, {
    method: "POST",
    body: JSON.stringify(body),
  })) as ApiAiAlgorithmGate;
}

export async function cloudAiClimateSetChannel(
  projectId: string,
  body: { entity_id: string; channel: "A" | "B" | "C" },
) {
  return (await authedFetch(`/ai-center/climate/${projectId}/channel`, {
    method: "POST",
    body: JSON.stringify(body),
  })) as {
    entity_id: string;
    channel: "A" | "B" | "C";
    capability_label: string;
  };
}

export async function cloudAiClimateEvaluate(
  projectId: string,
  body: { template_id?: string; params?: Partial<ApiClimateSleepParams> } = {},
) {
  return (await authedFetch(`/ai-center/climate/${projectId}/evaluate`, {
    method: "POST",
    body: JSON.stringify(body),
  })) as ApiClimateDraft;
}

export async function cloudAiClimatePrepare(
  projectId: string,
  body: {
    draft?: ApiClimateDraft;
    params?: Partial<ApiClimateSleepParams>;
  } = {},
) {
  return (await authedFetch(`/ai-center/climate/${projectId}/prepare`, {
    method: "POST",
    body: JSON.stringify(body),
  })) as ApiClimateDraft;
}

export type ApiClimateExecutorRole =
  | "ac"
  | "floor_heat"
  | "fresh_air"
  | "dehumidifier";

export type ApiClimateFactorLoop = {
  project_id: string;
  model: "minimal_factor_v1";
  entitlement: boolean;
  ecliptic_longitude: number;
  hemisphere: "north" | "south";
  geo: {
    latitude: number;
    longitude: number;
    elevation: number | null;
    source: string;
    query?: string;
  } | null;
  pipelines: Array<{
    role: ApiClimateExecutorRole;
    status: "written" | "skipped" | "blocked" | "gap";
    reason: string;
    entity_id?: string;
    matched_factors: Array<{
      key: string;
      label: string;
      category: string;
      layer: string;
    }>;
    setpoint?: number;
    intervene_above?: number;
    intervene_below?: number;
    entity_setpoint?: number;
    entity_intervene_above?: number;
    temp_unit?: "C" | "F";
    hvac_mode?: string;
    fan_mode?: string;
    swing_mode?: string;
    power_action?: string;
    power_reason?: string;
    power_detail?: string;
    temp_hysteresis?: number;
    yaml?: string;
    filename?: string;
    warnings: string[];
  }>;
  warnings: string[];
  evaluated_at: string;
  /** 本轮实际使用的感知输入（绑定优先，未绑走自动匹配） */
  sensor_inputs?: {
    temp: string | null;
    humidity: string | null;
    occupancy: string | null;
    doors: string[];
    bound: boolean;
  };
  /** 智能档位：因子数量等级 L0–L5 */
  smart_tier?: {
    level: 0 | 1 | 2 | 3 | 4 | 5;
    label: string;
    available: string[];
    missing: string[];
    factor_count: number;
    unsupported_rules: Array<{
      id: string;
      label: string;
      missing: string[];
    }>;
  };
  write_packages: Array<{
    role: ApiClimateExecutorRole;
    filename: string;
    yaml: string;
    alias: string;
  }>;
  revisions?: Array<{
    role: string;
    revision_id: string;
    filename: string;
    write_hint: {
      kind: "automation";
      name: string;
      yaml: string;
      filename: string;
      source: "ai_generated";
    };
  }>;
};

/** M1 最小因子评估（四执行器拆分，不落盘） */
export async function cloudAiClimateFactorEvaluate(
  projectId: string,
  body: {
    hemisphere?: "north" | "south";
    ecliptic_longitude?: number;
    force_geo_refresh?: boolean;
  } = {},
) {
  return (await authedFetch(
    `/ai-center/climate/${projectId}/factor-evaluate`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  )) as ApiClimateFactorLoop;
}

/** M1 最小因子先写 draft；再 config-write 后发 */
export async function cloudAiClimateFactorPrepare(
  projectId: string,
  body: {
    hemisphere?: "north" | "south";
    ecliptic_longitude?: number;
    force_geo_refresh?: boolean;
    /** 测试例外：空调关也可写待命包；运营点下发传 true */
    allow_standby?: boolean;
  } = {},
) {
  return (await authedFetch(
    `/ai-center/climate/${projectId}/factor-prepare`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  )) as ApiClimateFactorLoop;
}

export type ApiClimateProactiveStatus = {
  phase: "watch" | "suggest" | "powering" | "ai";
  suggest: {
    message: string;
    room: string | null;
    temp_c: number | null;
    humidity_pct: number | null;
    occupied: boolean;
    ac_entity_id: string;
    reasons: string[];
  } | null;
  suggest_at: number | null;
  last_suggest_at: number | null;
  last_apply_at: number | null;
  last_result: string | null;
  was_running: boolean;
  powering_entity_id: string | null;
  main_path: string;
  suggest_optional: boolean;
};

export async function cloudAiClimateProactiveStatus(projectId: string) {
  return (await authedFetch(
    `/ai-center/climate/${projectId}/proactive/status`,
  )) as ApiClimateProactiveStatus;
}

export async function cloudAiClimateAuthorizePower(
  projectId: string,
  body: { entity_id?: string } = {},
) {
  return (await authedFetch(
    `/ai-center/climate/${projectId}/proactive/authorize-power`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  )) as {
    status: string;
    entity_id: string;
    command_id: string | null;
    apply: { status: string; command_ids: string[]; message?: string } | null;
    message: string;
  };
}

export async function cloudAiClimateBaseline(projectId: string) {
  return (await authedFetch(
    `/ai-center/climate/${projectId}/baseline`,
  )) as ApiClimateBaseline;
}

export async function cloudAiClimateSetBaseline(
  projectId: string,
  body: { enabled: boolean; climate_entity_ids?: string[] },
) {
  return (await authedFetch(`/ai-center/climate/${projectId}/baseline`, {
    method: "POST",
    body: JSON.stringify(body),
  })) as ApiClimateBaseline;
}

export async function cloudAiClimateSampleBaseline(projectId: string) {
  return (await authedFetch(
    `/ai-center/climate/${projectId}/baseline/sample`,
    { method: "POST", body: "{}" },
  )) as {
    added: number;
    sample_count: number;
    last_sample_at: string;
    note?: string;
  };
}

export type ApiClimatePowerThresholds = {
  unoccupied_off_slack_c: number;
  temp_hysteresis_min_c: number;
  temp_hysteresis_default_c: number;
  /** 项目显式回温差（0～4，含 0）；未配置为 null */
  project_temp_hysteresis?: number | null;
};

export type ApiClimatePowerThresholdOverrides = {
  /** 回温差 ℃；0=无温差；1～4=±该度数；null=清除继承 */
  temp_hysteresis?: number | null;
};

export type ApiClimatePowerThresholdsDto = {
  effective: ApiClimatePowerThresholds;
  overrides: ApiClimatePowerThresholdOverrides | null;
  platform: ApiClimatePowerThresholds;
  scope: "project";
  priority: string;
};

/** GET 项目级回温差（含平台对照） */
export async function cloudAiClimatePowerThresholds(projectId: string) {
  return (await authedFetch(
    `/ai-center/climate/${projectId}/power-thresholds`,
  )) as ApiClimatePowerThresholdsDto;
}

/**
 * PUT 项目回温差。temp_hysteresis: 0～4（0=无温差）；null / clear_all 继承平台。
 * 仅集成商管理员可写。
 */
export async function cloudAiClimateSetPowerThresholds(
  projectId: string,
  body: ApiClimatePowerThresholdOverrides & { clear_all?: boolean },
) {
  return (await authedFetch(
    `/ai-center/climate/${projectId}/power-thresholds`,
    { method: "PUT", body: JSON.stringify(body) },
  )) as ApiClimatePowerThresholdsDto;
}

export async function cloudAiListDrafts(
  projectId: string,
  kind?: "scene" | "automation",
) {
  const qs = kind ? `?kind=${encodeURIComponent(kind)}` : "";
  const data = await authedFetch(`/ai-center/drafts/${projectId}${qs}`);
  const payload = data as { items?: ApiAiDraft[] } | ApiAiDraft[];
  if (Array.isArray(payload)) return payload;
  return payload.items ?? [];
}

export async function cloudAiGetDraft(projectId: string, revisionId: string) {
  const data = await authedFetch(
    `/ai-center/drafts/${projectId}/${revisionId}`,
  );
  return data as ApiAiDraft;
}

export async function cloudAiDeleteDraft(projectId: string, revisionId: string) {
  await authedFetch(`/ai-center/drafts/${projectId}/${revisionId}`, {
    method: "DELETE",
  });
  return { ok: true as const };
}

/** 接受草稿：服务端再次校验后经写门禁热加载，不重启主机。 */
export async function cloudAiAcceptDraft(
  projectId: string,
  draftId: string,
  body: { yaml?: string } = {},
) {
  return (await authedFetch(
    `/ai-center/drafts/${projectId}/${draftId}/accept`,
    { method: "POST", body: JSON.stringify(body) },
  )) as {
    status: string;
    command_id: string;
    revision_id: string;
    filename: string;
    reload_services: string[];
    diff: string;
    notice: string;
  };
}

export async function cloudAiApplyDraft(
  projectId: string,
  draft: Pick<ApiAiDraft, "id" | "yaml" | "content">,
) {
  return cloudAiAcceptDraft(projectId, draft.id, {
    yaml: draft.yaml || draft.content || "",
  });
}

// ─── Project-scoped Aqara API ───────────────────────────

export type AqaraConnectionStatus = {
  id?: string;
  appId?: string;
  domain?: string;
  status: "app_not_configured" | "not_authorized" | "connected" | "expired";
  openId?: string | null;
  tokenExpiresAt?: string | null;
  webhookUrl?: string | null;
  families?: { positionId: string; positionName: string | null }[];
};

export type AqaraProjectFamily = {
  id: string;
  projectId: string;
  positionId: string;
  positionName: string | null;
};

export type AqaraAppConfigDto = {
  appId: string;
  keyId: string;
  /** 首次保存必填；已有配置时留空即保留已加密的 App Key。 */
  appKey?: string;
  domain: string;
  webhookUrl?: string;
};

export type AqaraAppConfigPublicDto = Omit<AqaraAppConfigDto, "appKey"> & {
  appKeySet: boolean;
};

export type AqaraPosition = {
  id: string;
  positionId: string;
  positionName: string | null;
  parentId: string | null;
  deviceCount: number;
  lastSyncedAt: string | null;
};

export type AqaraDevice = {
  did: string;
  model: string;
  deviceName: string;
  positionId: string;
  positionName?: string;
  online: boolean;
  /** false = scene_fallback 等无注册表 state，勿当离线 */
  onlineKnown?: boolean;
  state?: number | null;
  parentDid?: string;
  /** 1 可挂子设备网关 / 2 网关 / 3 子设备（开放文档） */
  modelType?: number | null;
  firmwareVersion?: string | null;
  timeZone?: string | null;
  joinedAt?: string | null;
  deviceUpdatedAt?: string | null;
  subDeviceCount?: number;
};

export type AqaraResourceValue = {
  resourceId: string;
  value: string;
  name?: string;
  description?: string;
};

export type AqaraScene = {
  sceneId: string;
  sceneName: string;
  positionId: string;
  positionName?: string;
  model?: string;
  localized?: number;
  enabled?: boolean;
};

export type AqaraLinkage = {
  linkageId: string;
  linkageName: string;
  positionId: string;
  positionName?: string;
  model?: string;
  localized?: number;
  enabled: boolean;
};

export type AqaraEventLog = {
  id: string;
  msgType: string;
  eventType: string | null;
  subjectId: string | null;
  resourceId: string | null;
  value: string | null;
  statusCode: number | null;
  rawPayload: unknown;
  receivedAt: string;
};

function paq(path: string, projectId: string) {
  return `/projects/${encodeURIComponent(projectId)}/aqara${path}`;
}

export async function projectAqaraGetStatus(projectId: string) {
  const data = await authedFetch(paq("/status", projectId));
  return data as AqaraConnectionStatus | null;
}

export async function projectAqaraSendAuthCode(
  projectId: string,
  body: { account: string; accessTokenValidity?: string },
) {
  const data = await authedFetch(paq("/auth/code", projectId), {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data as { sent: boolean; authCode?: string };
}

export async function projectAqaraGetToken(
  projectId: string,
  body: { authCode: string; account: string },
) {
  const data = await authedFetch(paq("/auth/token", projectId), {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data as { status: string; openId?: string; expiresAt?: string };
}

export async function projectAqaraRefreshToken(projectId: string) {
  const data = await authedFetch(paq("/auth/refresh", projectId), {
    method: "POST",
  });
  return data as { status: string; expiresAt?: string };
}

export async function projectAqaraRevokeConnection(projectId: string) {
  const data = await authedFetch(paq("/connection", projectId), {
    method: "DELETE",
  });
  return data as { revoked: boolean };
}

export async function projectAqaraListPositions(projectId: string) {
  const data = await authedFetch(paq("/positions", projectId));
  return data as AqaraPosition[];
}

export async function projectAqaraSyncPositions(projectId: string) {
  const data = await authedFetch(paq("/positions/sync", projectId), {
    method: "POST",
  });
  return data as { synced: number; positions?: AqaraPosition[] };
}

export async function projectAqaraListRootPositions(projectId: string) {
  const data = await authedFetch(paq("/positions/roots", projectId));
  return data as AqaraPosition[];
}

export async function projectAqaraGetFamilies(projectId: string) {
  const data = await authedFetch(paq("/families", projectId));
  return data as AqaraProjectFamily[];
}

export async function projectAqaraSetFamilies(
  projectId: string,
  positionIds: string[],
) {
  const data = await authedFetch(paq("/families", projectId), {
    method: "PUT",
    body: JSON.stringify({ positionIds }),
  });
  return data as { selected: number };
}

export async function projectAqaraListDevices(
  projectId: string,
  positionId?: string,
) {
  const q = positionId
    ? `?positionId=${encodeURIComponent(positionId)}`
    : "";
  const data = await authedFetch(paq(`/devices${q}`, projectId));
  return data as {
    items: AqaraDevice[];
    totalCount?: number;
    /** Always cache for GET; harvestSource is registry | scene_fallback from last sync */
    source?: "cache" | "registry" | "scene_fallback";
    harvestSource?: "registry" | "scene_fallback";
    syncedAt?: string | null;
    needsSync?: boolean;
  };
}

/** Pull manufacturer devices into project mirror (slow; use Sync button). */
export async function projectAqaraSyncDevices(projectId: string) {
  const data = await authedFetch(paq("/devices/sync", projectId), {
    method: "POST",
  });
  return data as {
    synced: number;
    source?: "registry" | "scene_fallback";
    syncedAt?: string;
    items: AqaraDevice[];
    totalCount?: number;
  };
}

/** OpenAPI config.device.name */
export async function projectAqaraRenameDevice(
  projectId: string,
  did: string,
  name: string,
) {
  const data = await authedFetch(
    paq(`/devices/${encodeURIComponent(did)}`, projectId),
    { method: "PUT", body: JSON.stringify({ name }) },
  );
  return data as { ok: boolean };
}

/** OpenAPI config.device.position */
export async function projectAqaraMoveDevices(
  projectId: string,
  dids: string[],
  positionId: string,
) {
  const data = await authedFetch(paq("/devices/move", projectId), {
    method: "POST",
    body: JSON.stringify({ dids, positionId }),
  });
  return data as { ok: boolean; moved: number };
}

/** OpenAPI write.device.unbind */
export async function projectAqaraUnbindDevice(projectId: string, did: string) {
  const data = await authedFetch(
    paq(`/devices/${encodeURIComponent(did)}`, projectId),
    { method: "DELETE" },
  );
  return data as { ok: boolean };
}

export async function projectAqaraGetDeviceResources(
  projectId: string,
  did: string,
) {
  const data = await authedFetch(
    paq(`/devices/${encodeURIComponent(did)}`, projectId),
  );
  return data as { items: AqaraResourceValue[] };
}

export async function projectAqaraGetSubDevices(
  projectId: string,
  did: string,
) {
  const data = await authedFetch(
    paq(`/devices/${encodeURIComponent(did)}/sub`, projectId),
  );
  return data as { items: AqaraDevice[] };
}

export async function projectAqaraControlDevice(
  projectId: string,
  did: string,
  resources: { resourceId: string; value: string }[],
) {
  const data = await authedFetch(
    paq(`/devices/${encodeURIComponent(did)}/control`, projectId),
    {
      method: "POST",
      body: JSON.stringify({ resources }),
    },
  );
  return data as { ok: boolean };
}

export async function projectAqaraListScenes(
  projectId: string,
  positionId?: string,
) {
  const q = positionId
    ? `?positionId=${encodeURIComponent(positionId)}`
    : "";
  const data = await authedFetch(paq(`/scenes${q}`, projectId));
  return data as {
    items: AqaraScene[];
    totalCount?: number;
    source?: "cache";
    syncedAt?: string | null;
    needsSync?: boolean;
  };
}

/** Pull manufacturer scenes into project mirror (paginated OpenAPI). */
export async function projectAqaraSyncScenes(projectId: string) {
  const data = await authedFetch(paq("/scenes/sync", projectId), {
    method: "POST",
  });
  return data as {
    synced: number;
    syncedAt?: string;
    items: AqaraScene[];
    totalCount?: number;
  };
}

export async function projectAqaraRunScene(
  projectId: string,
  sceneId: string,
) {
  const data = await authedFetch(
    paq(`/scenes/${encodeURIComponent(sceneId)}/run`, projectId),
    { method: "POST" },
  );
  return data as { ok: boolean };
}

export async function projectAqaraDeleteScene(
  projectId: string,
  sceneId: string,
) {
  const data = await authedFetch(
    paq(`/scenes/${encodeURIComponent(sceneId)}`, projectId),
    { method: "DELETE" },
  );
  return data as { ok: boolean };
}

export async function projectAqaraListLinkages(
  projectId: string,
  positionId?: string,
) {
  const q = positionId
    ? `?positionId=${encodeURIComponent(positionId)}`
    : "";
  const data = await authedFetch(paq(`/linkages${q}`, projectId));
  return data as {
    items: AqaraLinkage[];
    totalCount?: number;
    source?: "cache";
    syncedAt?: string | null;
    needsSync?: boolean;
  };
}

/** Pull manufacturer linkages into project mirror. */
export async function projectAqaraSyncLinkages(projectId: string) {
  const data = await authedFetch(paq("/linkages/sync", projectId), {
    method: "POST",
  });
  return data as {
    synced: number;
    syncedAt?: string;
    items: AqaraLinkage[];
    totalCount?: number;
  };
}

/** Sync scenes + linkages together (联动「同步」). */
export async function projectAqaraSyncLinkageAll(projectId: string) {
  const data = await authedFetch(paq("/linkage/sync", projectId), {
    method: "POST",
  });
  return data as {
    scenes: number;
    linkages: number;
    syncedAt?: string;
  };
}

export async function projectAqaraToggleLinkage(
  projectId: string,
  linkageId: string,
  enable: boolean,
) {
  const data = await authedFetch(
    paq(`/linkages/${encodeURIComponent(linkageId)}/toggle`, projectId),
    {
      method: "POST",
      body: JSON.stringify({ enable }),
    },
  );
  return data as { ok: boolean };
}

export async function projectAqaraDeleteLinkage(
  projectId: string,
  linkageId: string,
) {
  const data = await authedFetch(
    paq(`/linkages/${encodeURIComponent(linkageId)}`, projectId),
    { method: "DELETE" },
  );
  return data as { ok: boolean };
}

export async function projectAqaraListEvents(
  projectId: string,
  params?: {
    limit?: number;
    since?: string;
    type?: string;
    subjectId?: string;
  },
) {
  const q = new URLSearchParams();
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.since) q.set("since", params.since);
  if (params?.type) q.set("type", params.type);
  if (params?.subjectId) q.set("subjectId", params.subjectId);
  const qs = q.toString();
  const data = await authedFetch(paq(`/events${qs ? `?${qs}` : ""}`, projectId));
  return data as AqaraEventLog[];
}

/* ─── B21-10 施工验收 ─── */

export type AcceptanceTemplateDto = {
  id: string;
  std_code: string;
  std_version: string;
  status: string;
  architecture_family: string;
  stage_code: string;
  stage_label?: string;
  category: string;
  item_name: string;
  std_value: string;
  check_method?: string | null;
  required_tools?: string | null;
  field_type: string;
  field_options?: string[] | null;
  sort_order: number;
  published_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type AcceptanceItemDto = {
  id: string;
  project_id: string;
  std_code: string;
  std_version: string;
  architecture_family: string;
  stage_code: string;
  stage_label?: string;
  category: string;
  item_name: string;
  std_value: string;
  check_method?: string | null;
  required_tools?: string | null;
  field_type: string;
  sort_order: number;
  result_status: string;
  result_value?: string | null;
  result_note?: string | null;
  photo_keys: string[];
  filled_by_name?: string | null;
  filled_at?: string | null;
};

export type AcceptanceSignoffDto = {
  id: string;
  scope: string;
  scope_label?: string;
  actor_kind: string;
  actor_name: string;
  note?: string | null;
  signed_at: string;
};

export type ProjectAcceptanceDto = {
  project_id: string;
  architecture_family: string | null;
  primary_protocol: string | null;
  acceptance_phase: string | null;
  delivery_status: string;
  instantiated: boolean;
  stages: Array<{
    id: string;
    label: string;
    total: number;
    pass: number;
    fail: number;
    pending: number;
    na: number;
  }>;
  items: AcceptanceItemDto[];
  signoffs: AcceptanceSignoffDto[];
  /** 与运营已发布模板的差异 */
  standards?: {
    published_count: number;
    snapshot_count: number;
    missing_count: number;
    stale_pending_count: number;
    in_sync: boolean;
  };
  sync?: { added: number; refreshed: number };
  meta: {
    architecture_families: Array<{ id: string; label: string }>;
    stages: Array<{ id: string; label: string }>;
    field_types: string[];
    result_statuses: string[];
  };
};

export async function cloudAdminListAcceptanceTemplates(params?: {
  architecture_family?: string;
  stage_code?: string;
  status?: string;
}) {
  const q = new URLSearchParams();
  if (params?.architecture_family) {
    q.set("architecture_family", params.architecture_family);
  }
  if (params?.stage_code) q.set("stage_code", params.stage_code);
  if (params?.status) q.set("status", params.status);
  const qs = q.toString();
  return (await authedFetch(
    `/admin/acceptance-templates${qs ? `?${qs}` : ""}`,
  )) as {
    items: AcceptanceTemplateDto[];
    meta: ProjectAcceptanceDto["meta"];
  };
}

export async function cloudAdminCreateAcceptanceTemplate(
  body: Record<string, unknown>,
) {
  return (await authedFetch("/admin/acceptance-templates", {
    method: "POST",
    body: JSON.stringify(body),
  })) as { item: AcceptanceTemplateDto };
}

export async function cloudAdminUpdateAcceptanceTemplate(
  id: string,
  body: Record<string, unknown>,
) {
  return (await authedFetch(`/admin/acceptance-templates/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  })) as { item: AcceptanceTemplateDto };
}

export async function cloudAdminPublishAcceptanceTemplate(id: string) {
  return (await authedFetch(
    `/admin/acceptance-templates/${encodeURIComponent(id)}/publish`,
    { method: "POST" },
  )) as { item: AcceptanceTemplateDto };
}

/** 升版保存：已发布/废弃 → 新草稿（版本 +0.1）；仅调用时落库。body 为编辑后字段 */
export async function cloudAdminReviseAcceptanceTemplate(
  id: string,
  body?: Record<string, unknown>,
) {
  return (await authedFetch(
    `/admin/acceptance-templates/${encodeURIComponent(id)}/revise`,
    { method: "POST", body: JSON.stringify(body || {}) },
  )) as {
    item: AcceptanceTemplateDto;
    reused_draft?: boolean;
    from_id?: string;
  };
}

export async function cloudAdminDeprecateAcceptanceTemplate(id: string) {
  return (await authedFetch(
    `/admin/acceptance-templates/${encodeURIComponent(id)}/deprecate`,
    { method: "POST" },
  )) as { item: AcceptanceTemplateDto };
}

export async function cloudGetProjectAcceptance(projectId: string) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/acceptance`,
  )) as ProjectAcceptanceDto;
}

export async function cloudInstantiateProjectAcceptance(
  projectId: string,
  body?: Record<string, unknown>,
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/acceptance/instantiate`,
    {
      method: "POST",
      body: JSON.stringify(body || {}),
    },
  )) as ProjectAcceptanceDto;
}

/** 补齐运营新增标准；刷新未填报项的规范文案（已填结果保留） */
export async function cloudSyncProjectAcceptanceStandards(projectId: string) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/acceptance/sync-standards`,
    { method: "POST" },
  )) as ProjectAcceptanceDto;
}

export async function cloudPatchProjectAcceptanceMeta(
  projectId: string,
  body: Record<string, unknown>,
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/acceptance/meta`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  )) as {
    project_id: string;
    architecture_family: string | null;
    primary_protocol: string | null;
    acceptance_phase: string | null;
    delivery_status: string;
  };
}

export async function cloudFillAcceptanceItem(
  projectId: string,
  itemId: string,
  body: Record<string, unknown>,
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/acceptance/items/${encodeURIComponent(itemId)}`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  )) as { item: AcceptanceItemDto };
}

export async function cloudIntegratorAcceptanceSignoff(
  projectId: string,
  body: Record<string, unknown>,
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/acceptance/signoff`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  )) as { signoff: AcceptanceSignoffDto };
}

// ─── Geek debug (米家极客旁路 · 22 号) ───────────────────

export type GeekDebugEligibility = {
  eligible: boolean;
  reasons: string[];
  accessChannel: string;
  installForm: string;
  hasGateway: boolean;
  agentOnline: boolean;
  gatewayId: string | null;
  sla: boolean;
  note?: string;
};

export type GeekDebugSession = {
  id: string;
  projectId: string;
  hubDid: string | null;
  purpose: string;
  channel: string;
  state: string;
  durationMinutes: number;
  authorizedBy: string | null;
  authorizationMode: string | null;
  endReason: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  updatedAt: string;
  operatorEmail: string | null;
  endsAt: string | null;
  remainingSec: number | null;
  elevatedSynced?: boolean;
  elevatedEndsAt?: string | null;
  loginCodeReady: boolean;
  channelLive: boolean;
  proxyPath?: string | null;
  lanUrl?: string | null;
  channelNote?: string;
};

export type GeekDebugHubTarget = {
  id: string;
  did: string;
  groupId: string | null;
  role: number | null;
  lastIp: string | null;
  webPort: number | null;
  suiteMqtt: boolean | null;
  name: string | null;
  firmware: string | null;
  source: string;
  lastSeenAt: string | null;
  verifiedAt: string | null;
  primaryHint: boolean;
};

export async function cloudGeekEligibility(projectId: string) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/eligibility`,
  )) as GeekDebugEligibility;
}

export async function cloudGeekTargets(projectId: string) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/targets`,
  )) as { items: GeekDebugHubTarget[] };
}

export async function cloudGeekDiscover(projectId: string, timeout = 10) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/discover`,
    { method: "POST", body: JSON.stringify({ timeout }) },
  )) as {
    commandId: string;
    status: string;
    error?: string | null;
    hubs: Array<Record<string, unknown>>;
    count: number;
  };
}

export async function cloudGeekCheckTarget(
  projectId: string,
  ip: string,
  ports?: number[],
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/check-target`,
    { method: "POST", body: JSON.stringify({ ip, ports }) },
  )) as Record<string, unknown>;
}

export async function cloudGeekSessions(projectId: string) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/sessions`,
  )) as { items: GeekDebugSession[] };
}

export async function cloudGeekCreateSession(
  projectId: string,
  body: { purpose: string; durationMinutes?: number; hubDid?: string },
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/sessions`,
    { method: "POST", body: JSON.stringify(body) },
  )) as GeekDebugSession;
}

export async function cloudGeekGetSession(projectId: string, sessionId: string) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/sessions/${encodeURIComponent(sessionId)}`,
  )) as GeekDebugSession;
}

export async function cloudGeekSessionDiscover(
  projectId: string,
  sessionId: string,
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/sessions/${encodeURIComponent(sessionId)}/discover`,
    { method: "POST", body: "{}" },
  )) as { session: GeekDebugSession; discover: Record<string, unknown> };
}

export async function cloudGeekPrepare(
  projectId: string,
  sessionId: string,
  body: {
    channel?: string;
    targetIp?: string;
    webPort?: number;
    hubDid?: string;
  },
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/sessions/${encodeURIComponent(sessionId)}/prepare`,
    { method: "POST", body: JSON.stringify(body) },
  )) as {
    commandId: string;
    status: string;
    prepare: Record<string, unknown>;
    tunnel?: { ok: boolean; proxyPath?: string; error?: string | null };
    session: GeekDebugSession;
  };
}

export async function cloudGeekAuthLink(projectId: string, sessionId: string) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/sessions/${encodeURIComponent(sessionId)}/auth-link`,
    { method: "POST", body: "{}" },
  )) as {
    token: string;
    path: string;
    projectName: string;
    purpose: string;
    durationMinutes: number;
    operator?: string | null;
    expiresInMinutes: number;
  };
}

export async function cloudGeekSubmitCode(
  projectId: string,
  sessionId: string,
  code: string,
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/sessions/${encodeURIComponent(sessionId)}/submit-code`,
    { method: "POST", body: JSON.stringify({ code }) },
  )) as GeekDebugSession;
}

export async function cloudGeekConnect(projectId: string, sessionId: string) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/sessions/${encodeURIComponent(sessionId)}/connect`,
    { method: "POST", body: "{}" },
  )) as GeekDebugSession;
}

export async function cloudGeekEndSession(
  projectId: string,
  sessionId: string,
  reason?: string,
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/sessions/${encodeURIComponent(sessionId)}/end`,
    { method: "POST", body: JSON.stringify({ reason }) },
  )) as GeekDebugSession;
}

export async function cloudGeekAudit(projectId: string, sessionId: string) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/sessions/${encodeURIComponent(sessionId)}/audit`,
  )) as { items: Array<{ id: string; ts: string; event: string; detail: unknown }> };
}

export async function cloudGeekArtifacts(projectId: string) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/artifacts`,
  )) as {
    items: Array<{
      id: string;
      sessionId: string | null;
      rawHash: string;
      rawBytes: number | null;
      fileName: string | null;
      note: string | null;
      capturedAt: string;
    }>;
  };
}

/** 同步云枢托管场景/自动化到小米中枢极客规则图（需 6 位码 + 主机 xgg） */
export async function cloudGeekSyncHubRule(
  projectId: string,
  body: {
    revisionId: string;
    loginCode: string;
    sessionId?: string;
    targetIp?: string;
    webPort?: number;
    enable?: boolean;
  },
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/sync-hub-rule`,
    { method: "POST", body: JSON.stringify(body) },
  )) as {
    ok: boolean;
    commandId?: string;
    revisionId: string;
    kind: string;
    baseUrl?: string;
    rule_id?: string;
    rule_name?: string;
    written?: number;
    enabled?: boolean;
    note?: string;
    skipped?: unknown[];
  };
}

export async function cloudGeekUploadBak(
  projectId: string,
  opts: {
    filename: string;
    dataBase64: string;
    sessionId?: string;
    note?: string;
  },
) {
  return (await authedFetch(
    `/projects/${encodeURIComponent(projectId)}/geek-debug/artifacts`,
    {
      method: "POST",
      body: JSON.stringify({
        filename: opts.filename,
        data: opts.dataBase64,
        sessionId: opts.sessionId,
        note: opts.note,
      }),
    },
  )) as {
    id: string;
    rawHash: string;
    rawBytes: number;
    fileName?: string;
    duplicate?: boolean;
  };
}

export async function publicGeekDebugPreview(token: string) {
  const res = await fetchWithTimeout(
    `${apiBase()}/public/geek-debug?token=${encodeURIComponent(token)}`,
  );
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  return unwrapApi(raw) as {
    session?: {
      id: string;
      projectName: string;
      purpose: string;
      operator: string;
      durationMinutes: number;
      state: string;
      loginCodeReady: boolean;
    };
    error?: string;
  };
}

export async function publicGeekDebugSubmitCode(token: string, code: string) {
  const res = await fetchWithTimeout(`${apiBase()}/public/geek-debug/submit-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, code }),
  });
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  return unwrapApi(raw) as { ok?: boolean; error?: string; state?: string };
}

export async function publicGeekDebugDeny(token: string) {
  const res = await fetchWithTimeout(`${apiBase()}/public/geek-debug/deny`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  const raw = await parseJson(res);
  if (!res.ok) throwApiError(raw, res.status);
  return unwrapApi(raw) as { ok?: boolean; error?: string; state?: string };
}

/* —— Partner Support (A.18)：集成商 ↔ 平台求助；与告警 tickets 分路径 —— */

export type SupportRequestCategory =
  | "account"
  | "billing"
  | "capability"
  | "debug_assist"
  | "other";

export type SupportRequestStatus =
  | "open"
  | "in_progress"
  | "waiting_integrator"
  | "resolved"
  | "cancelled";

export type SupportRequestEvent = {
  id: number;
  at: string;
  actor: string;
  actorRole?: string | null;
  kind: string;
  text: string;
};

export type SupportRequestDto = {
  id: string;
  tenantId: string;
  tenantName?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  title: string;
  category: SupportRequestCategory | string;
  description: string;
  priority: string;
  status: SupportRequestStatus | string;
  assignee?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string | null;
  /** Route when the integrator opened the help FAB */
  pagePath?: string | null;
  pageTitle?: string | null;
  events?: SupportRequestEvent[];
};

export async function cloudListSupportRequests(status?: string) {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  const data = await authedFetch(`/support-requests${q}`);
  return data as { items: SupportRequestDto[] };
}

export async function cloudGetSupportRequest(id: string) {
  const data = await authedFetch(`/support-requests/${encodeURIComponent(id)}`);
  return data as { request: SupportRequestDto };
}

export async function cloudCreateSupportRequest(body: {
  title: string;
  category: string;
  description: string;
  priority?: string;
  projectId?: string | null;
  pagePath?: string | null;
  pageTitle?: string | null;
}) {
  const data = await authedFetch("/support-requests", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data as { request: SupportRequestDto };
}

export async function cloudCommentSupportRequest(id: string, text: string) {
  const data = await authedFetch(
    `/support-requests/${encodeURIComponent(id)}/events`,
    { method: "POST", body: JSON.stringify({ text }) },
  );
  return data as { request: SupportRequestDto };
}

export async function cloudAdminListSupportRequests(status?: string) {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  const data = await authedFetch(`/admin/support-requests${q}`);
  return data as { items: SupportRequestDto[] };
}

export async function cloudAdminGetSupportRequest(id: string) {
  const data = await authedFetch(
    `/admin/support-requests/${encodeURIComponent(id)}`,
  );
  return data as { request: SupportRequestDto };
}

export async function cloudAdminPatchSupportRequest(
  id: string,
  patch: { status?: string; assignee?: string | null; assignSelf?: boolean },
) {
  const data = await authedFetch(
    `/admin/support-requests/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
  return data as { request: SupportRequestDto };
}

export async function cloudAdminCommentSupportRequest(id: string, text: string) {
  const data = await authedFetch(
    `/admin/support-requests/${encodeURIComponent(id)}/events`,
    { method: "POST", body: JSON.stringify({ text }) },
  );
  return data as { request: SupportRequestDto };
}
